import React, { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import {
  Button,
  Col,
  Row,
  Card,
  Alert,
  Badge,
  Modal,
  Toast,
  ToastContainer,
  Spinner,
  Form,
  Accordion,
  Table,
} from "react-bootstrap";
import {
  FaSearch, FaUsers ,
  FaShieldAlt,
  FaGlobe,
  FaTrash,
  FaClock,
  FaCheck,
  FaExclamationTriangle,
  FaChevronDown,
  FaChevronUp,
  FaKey,
  FaArrowRight,
  FaUserShield,
  FaFileAlt,
  FaDatabase,
  FaLock,
  FaHandshake,
  FaComments,
  FaTimes,
  FaDownload,
  FaFilePdf,
  FaLayerGroup,
  FaUserFriends,
  FaBalanceScale,
  FaMapMarkerAlt,
  FaBuilding,
  FaCalendarAlt,
  FaInfoCircle,
} from "react-icons/fa";
import BackgroundFooter from "../BackgroundFooter";
import "../../ComponentsCss/Iam.css";
import "../../ComponentsCss/DPDP.css";
import Menubar from "../Menubar";
import NavigationBar from "../Nav";
import { useAuth } from "../../contexts/AuthContext";

/* ── India DPDP child components (same folder as this file) ── */
import SecurityEncryptionTab from "./SecurityEncryptionTab";
import CrossBorderTransfersTab from "./CrossBorderTransfersTab";
import DPDPInventoryTab from "./DPDPInventoryTab";
import DPDPRetentionTab from "./DPDPRetentionTab";
import PolicyGeneratorTab from "./PolicyGeneratorTab";
import DsarManagementTab from "./DsarManagementTab";
import ConsentManagementTab from "./ConsentManagementTab";
import GrievanceManagementTab from "./GrievanceManagementTab";
import DPOManagementTab from "./DPOManagementTab";

/* ── GDPR child components (sibling folder ../GDPR/) ── */
import GdprSecurityEncryptionTab from "../GDPR/GDPRSecurityEncryptionTab";
import GdprCrossBorderTransfersTab from "../GDPR/GdprCrossBorderTransfersTab";
import GDPRInventoryTab from "../GDPR/GDPRInventoryTab";
import GDPRRetentionTab from "../GDPR/GDPRRetentionTab";
import GdprPolicyGeneratorTab from "../GDPR/GdprPolicyGeneratorTab";
import GdprDsarManagementTab from "../GDPR/GdprDsarManagementTab";
import GdprConsentManagementTab from "../GDPR/GdprConsentManagementTab";


/* ── CCPA child components (sibling folder ../CCPA/) ── */
import CCPASecurityEncryptionTab from "../CCPA/CCPASecurityEncryptionTab";
import CCPAOptOutConsent from "../CCPA/CCPAOptOutConsent";
import CCPAInventoryTab from "../CCPA/CCPAInventoryTab";
import CCPARetentionTab from "../CCPA/CCPARetentionTab";
import CCPAPolicyGeneratorTab from "../CCPA/CCPAPolicyGeneratorTab";
import CCPADsarManagementTab from "../CCPA/CCPADsarManagementTab";

/* ── UAE PDPL child components (sibling folder ../UAE PDPL/) ── */
import UaeSecurityEncryptionTab from "../UAE PDPL/UaeSecurityEncryptionTab";
import UaeCrossBorderTransfersTab from "../UAE PDPL/UaeCrossBorderTransfersTab";
import UAEInventoryTab from "../UAE PDPL/UAEInventoryTab";
import UaeRetentionTab from "../UAE PDPL/UaeRetentionTab";
import UaePolicyGeneratorTab from "../UAE PDPL/UaePolicyGeneratorTab";
import UaeDsarManagementTab from "../UAE PDPL/UaeDsarManagementTab";
import UaeConsentManagementTab from "../UAE PDPL/UaeConsentManagementTab";
import { logActivity } from "../../utils/activityLogApi";

/* ═══════════════════════════════════════════════════════════════════
 * REGULATION CONTEXT
 * Every child component can call useRegulation() to know which
 * regulation is active and adapt its API calls / labels / logic.
 * ═══════════════════════════════════════════════════════════════════ */
const RegulationContext = createContext({
  activeRegulation: "dpdp",
  regulationConfig: {},
  setActiveRegulation: () => {},
});

export const useRegulation = () => useContext(RegulationContext);

/* ═══════════════════════════════════════════════════════════════════
 * REGULATION CONFIGURATIONS
 * Each regulation has its own:
 *   - API base URLs
 *   - Labels & legal references
 *   - Penalty info
 *   - Module definitions (which modules apply)
 *   - Regulator info
 *   - Feature flags (e.g., SDF only applies to DPDP)
 * ═══════════════════════════════════════════════════════════════════ */
const REGULATION_CONFIGS = {
  dpdp: {
    key: "dpdp",
    label: "DPDP Act",
    fullName: "India Digital Personal Data Protection Act, 2023",
    flag: "🇮🇳",
    country: "India",
    color: "#FF9933",
    authType: "org",
    /* API endpoints — your existing DPDP APIs */
    api: {
      activitiesList:
        "https://pb76qxphrf.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/activities/list",
      activitiesClear:
        "https://2sbj5wg765.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/activities/clear",
      subscriptionStatus:
        "https://atw24p2ef8.execute-api.ap-southeast-2.amazonaws.com/prod/api/org/subscription-status",
      startTrial:
        "https://1mjbhlptp2.execute-api.eu-west-1.amazonaws.com/prod/api/billing/start-trial",
      startSecondTrial:
        "https://1h9f9ci9rl.execute-api.eu-west-1.amazonaws.com/prod/api/billing/start-second-trial",
      activateKey:
        "https://r1rt2tzf87.execute-api.eu-west-1.amazonaws.com/prod/api/subscription/activate-key",
    },

    /* Regulator */
    regulator: {
      name: "Data Protection Board of India (DPBI)",
      maxPenalty: "₹250 Crore",
      description:
        "The Data Protection Board of India (DPBI) is the statutory authority enforcing the DPDP Act, 2023. The Board has the power to investigate breaches, adjudicate complaints, impose penalties up to ₹250 crore, and issue corrective directions to ensure compliance.",
    },

    /* Penalty structure */
    penalties: [
      {
        violation: "Security Breach",
        section: "§85",
        maxCr: 250,
        metricKey: "securityMeasures",
      },
      {
        violation: "Breach Notification",
        section: "§86",
        maxCr: 200,
        metricKey: "dparManagement",
      },
      {
        violation: "Children's Data",
        section: "§9",
        maxCr: 200,
        metricKey: null,
      },
      {
        violation: "SDF Obligations",
        section: "§10",
        maxCr: 150,
        metricKey: null,
        sdfOnly: true,
      },
      {
        violation: "Cross-Border",
        section: "§16",
        maxCr: 50,
        metricKey: "crossBorderTransfers",
      },
      {
        violation: "Other Breaches",
        section: "General",
        maxCr: 50,
        metricKey: null,
      },
    ],
    penaltyCalc: (overallScore, isSDF) => {
      const riskMul =
        overallScore > 90
          ? 0.05
          : overallScore > 80
            ? 0.1
            : overallScore > 70
              ? 0.2
              : overallScore > 60
                ? 0.35
                : overallScore > 50
                  ? 0.5
                  : overallScore > 40
                    ? 0.65
                    : 0.8;
      const est =
        (250 * 0.3 +
          200 * 0.2 +
          200 * 0.15 +
          (isSDF ? 150 : 0) * 0.2 +
          50 * 0.15) *
        riskMul;
      return {
        est,
        min: est * 0.3,
        max: est * 1.5,
        level:
          overallScore > 80 ? "Low" : overallScore > 60 ? "Medium" : "High",
        currency: "₹",
        unit: "Cr",
      };
    },

    /* Feature flags */
    hasSDF: true,
    hasGrievance: true,
    hasDPO: true,

    /* Modules available for this regulation */
    modules: [
      {
        key: "encryption",
        label: "Security Measures",
        icon: FaLock,
        color: "#7c3aed",
        metricKey: "securityMeasures",
        subText: (v) =>
          v > 80 ? "Controls healthy" : "Controls need attention",
      },
      {
        key: "crossBorder",
        label: "Cross-Border Transfers",
        icon: FaGlobe,
        color: "#0d6efd",
        metricKey: "crossBorderTransfers",
        subText: (v) => (v > 0 ? "Transfers tracked" : "No transfers recorded"),
      },
      {
        key: "inventory",
        label: "Data Inventory & Mapping",
        icon: FaDatabase,
        color: "#0891b2",
        metricKey: "dataMapping",
        subText: (v) => (v > 0 ? "Assets mapped" : "Not configured"),
      },
      {
        key: "retention",
        label: "Retention Policies",
        icon: FaClock,
        color: "#d97706",
        metricKey: "retentionPolicies",
        subText: (v) => (v > 0 ? "Policies configured" : "Policies missing"),
      },
      {
        key: "dpar",
        label: "DPAR Management",
        icon: FaFileAlt,
        color: "#64748b",
        metricKey: "dparManagement",
        subText: (v) => (v > 0 ? "Requests managed" : "Not configured"),
      },
      {
        key: "consent",
        label: "Consent Management",
        icon: FaHandshake,
        color: "#f59e0b",
        metricKey: "consentManagement",
        subText: (v) =>
          v > 0 ? "Consents captured" : "Notices pending review",
      },
      {
        key: "grievance",
        label: "Grievance Redressal",
        icon: FaComments,
        color: "#6366f1",
        metricKey: "grievanceRedressal",
        subText: (v) => (v > 0 ? "Grievances handled" : "Manual process only"),
      },
    ],

    /* Child component mapping */
    components: {
      /* SHARED — these same components are reused by GDPR/CCPA/PDPL */
      encryption: (props) => <SecurityEncryptionTab {...props} />,
      crossBorder: (props) => <CrossBorderTransfersTab {...props} />,
      inventory: (props) => <DPDPInventoryTab {...props} />,
      retention: (props) => <DPDPRetentionTab {...props} />,
      policyGen: (props) => <PolicyGeneratorTab {...props} />,
      /* SEPARATE — DPDP-specific components */
      dpar: (props) => <DsarManagementTab {...props} />,
      consent: (props) => <ConsentManagementTab {...props} />,
      grievance: (props) => <GrievanceManagementTab {...props} />,
      sdf: (props) => <DPOManagementTab {...props} />,
    },
  },

  gdpr: {
    key: "gdpr",
    label: "GDPR",
    fullName: "EU General Data Protection Regulation",
    flag: "🇪🇺",
    country: "European Union",
    color: "#003399",
    authType: "user",
    /* API endpoints — replace with actual GDPR endpoints when available */
    api: {
      activitiesList:
        "https://8j1rni4in1.execute-api.ap-southeast-2.amazonaws.com/prod/api/activity-log/list",
      activitiesClear: null,
      subscriptionStatus: null,
      startTrial: null,
      startSecondTrial: null,
      activateKey: null,
    },

    regulator: {
      name: "National Data Protection Authorities (DPAs)",
      maxPenalty: "€20M or 4% of global turnover",
      description:
        "GDPR is enforced by independent Data Protection Authorities in each EU member state. The lead DPA is determined by the location of the data controller's main establishment. Penalties can reach €20 million or 4% of annual global turnover, whichever is higher.",
    },

    penalties: [
      {
        violation: "Infringements of basic principles",
        section: "Art 83(5)",
        maxCr: "€20M / 4%",
        metricKey: null,
      },
      {
        violation: "Controller/processor obligations",
        section: "Art 83(4)",
        maxCr: "€10M / 2%",
        metricKey: null,
      },
      {
        violation: "Data transfer violations",
        section: "Art 83(5)",
        maxCr: "€20M / 4%",
        metricKey: "crossBorderTransfers",
      },
      {
        violation: "Failure to comply with DPA order",
        section: "Art 83(6)",
        maxCr: "€20M / 4%",
        metricKey: null,
      },
    ],
    penaltyCalc: (overallScore) => {
      const level =
        overallScore > 80 ? "Low" : overallScore > 60 ? "Medium" : "High";
      return { est: 0, min: 0, max: 0, level, currency: "€", unit: "M" };
    },

    hasSDF: false,
    hasGrievance: false,
    hasDPO: true,

    modules: [
      {
        key: "encryption",
        label: "Security Measures",
        icon: FaLock,
        color: "#7c3aed",
        metricKey: "securityMeasures",
        subText: (v) =>
          v > 80 ? "Controls healthy" : "Controls need attention",
      },
      {
        key: "crossBorder",
        label: "Cross-Border Transfers",
        icon: FaGlobe,
        color: "#0d6efd",
        metricKey: "crossBorderTransfers",
        subText: (v) => (v > 0 ? "Transfers tracked" : "No transfers recorded"),
      },
      {
        key: "inventory",
        label: "Data Inventory & Mapping",
        icon: FaDatabase,
        color: "#0891b2",
        metricKey: "dataMapping",
        subText: (v) =>
          v > 0 ? "Records of processing mapped" : "Not configured",
      },
      {
        key: "retention",
        label: "Retention Policies",
        icon: FaClock,
        color: "#d97706",
        metricKey: "retentionPolicies",
        subText: (v) => (v > 0 ? "Policies configured" : "Policies missing"),
      },
      {
        key: "dpar",
        label: "DSAR Management",
        icon: FaFileAlt,
        color: "#64748b",
        metricKey: "dparManagement",
        subText: (v) => (v > 0 ? "Subject requests managed" : "Not configured"),
      },
      {
        key: "consent",
        label: "Consent Management",
        icon: FaHandshake,
        color: "#f59e0b",
        metricKey: "consentManagement",
        subText: (v) => (v > 0 ? "Consents captured" : "Lawful basis pending"),
      },
    ],

    components: {
      /* GDPR-specific components */
      encryption: (props) => <GdprSecurityEncryptionTab {...props} />,
      crossBorder: (props) => <GdprCrossBorderTransfersTab {...props} />,
      inventory: (props) => <GDPRInventoryTab {...props} />,
      retention: (props) => <GDPRRetentionTab {...props} />,
      policyGen: (props) => <GdprPolicyGeneratorTab {...props} />,
 
     consent: (props) => <GdprConsentManagementTab {...props} />,
             dpar: (props) => <GdprDsarManagementTab {...props} />,  
      sdf: (props) => <DPOManagementTab {...props} regulation="gdpr" />,
    },
  },

  ccpa: {
    key: "ccpa",
    label: "CCPA",
    fullName: "California Consumer Privacy Act (as amended by CPRA)",
    flag: "🇺🇸",
    country: "United States (California)",
    color: "#B31942",
    authType: "user",
    api: {
      activitiesList:
        "https://cthceyv68h.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/activities/list",
      activitiesClear: null,
      subscriptionStatus: null,
      startTrial: null,
      startSecondTrial: null,
      activateKey: null,
    },

    regulator: {
      name: "California Privacy Protection Agency (CPPA)",
      maxPenalty: "$7,500 per intentional violation",
      description:
        "The California Privacy Protection Agency (CPPA) is responsible for implementing and enforcing the CCPA/CPRA. Penalties are $2,500 per unintentional violation and $7,500 per intentional violation, with no cap on total amount.",
    },

    penalties: [
      {
        violation: "Intentional violation",
        section: "§1798.155",
        maxCr: "$7,500/violation",
        metricKey: null,
      },
      {
        violation: "Unintentional violation",
        section: "§1798.155",
        maxCr: "$2,500/violation",
        metricKey: null,
      },
      {
        violation: "Minor data violation",
        section: "§1798.199.90",
        maxCr: "$7,500/minor",
        metricKey: null,
      },
    ],
    penaltyCalc: (overallScore) => {
      const level =
        overallScore > 80 ? "Low" : overallScore > 60 ? "Medium" : "High";
      return { est: 0, min: 0, max: 0, level, currency: "$", unit: "" };
    },

    hasSDF: false,
    hasGrievance: false,
    hasDPO: false,

    modules: [
      {
        key: "encryption",
        label: "Security Measures",
        icon: FaLock,
        color: "#7c3aed",
        metricKey: "securityMeasures",
        subText: (v) =>
          v > 80 ? "Controls healthy" : "Controls need attention",
      },
      {
        key: "crossBorder",
        label: "Cross-Border Transfers",
        icon: FaGlobe,
        color: "#0d6efd",
        metricKey: "crossBorderTransfers",
        subText: (v) => (v > 0 ? "Transfers tracked" : "No transfers recorded"),
      },
      {
        key: "inventory",
        label: "Data Inventory",
        icon: FaDatabase,
        color: "#0891b2",
        metricKey: "dataMapping",
        subText: (v) =>
          v > 0 ? "Personal info categories mapped" : "Not configured",
      },
      {
        key: "retention",
        label: "Retention Policies",
        icon: FaClock,
        color: "#d97706",
        metricKey: "retentionPolicies",
        subText: (v) => (v > 0 ? "Policies configured" : "Policies missing"),
      },
      {
        key: "dpar",
        label: "Consumer Request Management",
        icon: FaFileAlt,
        color: "#64748b",
        metricKey: "dparManagement",
        subText: (v) =>
          v > 0 ? "Consumer requests managed" : "Not configured",
      },
      {
        key: "consent",
        label: "Opt-Out / Do Not Sell",
        icon: FaHandshake,
        color: "#f59e0b",
        metricKey: "consentManagement",
        subText: (v) =>
          v > 0 ? "Opt-outs tracked" : "Opt-out notices pending",
      },
    ],

    components: {
      /* CCPA-specific components */
      encryption: (props) => <CCPASecurityEncryptionTab {...props} />,
      
      inventory: (props) => <CCPAInventoryTab {...props} />,
      retention: (props) => <CCPARetentionTab {...props} />,
      policyGen: (props) => <CCPAPolicyGeneratorTab {...props} />,
      dpar: (props) => <CCPADsarManagementTab {...props} />,
     consent: (props) => <CCPAOptOutConsent {...props} />,
    },
  },

  pdpl: {
    key: "pdpl",
    label: "UAE PDPL",
    fullName:
      "UAE Personal Data Protection Law (Federal Decree-Law No. 45 of 2021)",
    flag: "🇦🇪",
    country: "United Arab Emirates",
    color: "#00732F",
    authType: "user",
    api: {
      activitiesList: `https://ejiszrhioj.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/activity-log/list?t=${Date.now()}`,
      activitiesClear: null,
      subscriptionStatus: null,
      startTrial: null,
      startSecondTrial: null,
      activateKey: null,
    },

    regulator: {
      name: "UAE Data Office",
      maxPenalty: "AED 5,000,000",
      description:
        "The UAE Data Office under the Ministry of Cabinet Affairs is the competent authority for data protection. The PDPL imposes penalties up to AED 5 million for violations, with criminal liability for unauthorized disclosure of sensitive data.",
    },

    penalties: [
      {
        violation: "Non-compliance with data processing principles",
        section: "Art 26",
        maxCr: "AED 5M",
        metricKey: null,
      },
      {
        violation: "Failure to notify data breach",
        section: "Art 21",
        maxCr: "AED 5M",
        metricKey: null,
      },
      {
        violation: "Cross-border transfer violations",
        section: "Art 22",
        maxCr: "AED 5M",
        metricKey: "crossBorderTransfers",
      },
    ],
    penaltyCalc: (overallScore) => {
      const level =
        overallScore > 80 ? "Low" : overallScore > 60 ? "Medium" : "High";
      return { est: 0, min: 0, max: 0, level, currency: "AED", unit: "M" };
    },

    hasSDF: false,
    hasGrievance: false,
    hasDPO: true,

    modules: [
      {
        key: "encryption",
        label: "Security Measures",
        icon: FaLock,
        color: "#7c3aed",
        metricKey: "securityMeasures",
        subText: (v) =>
          v > 80 ? "Controls healthy" : "Controls need attention",
      },
      // {
      //   key: "crossBorder",
      //   label: "Cross-Border Transfers",
      //   icon: FaGlobe,
      //   color: "#0d6efd",
      //   metricKey: "crossBorderTransfers",
      //   subText: (v) => (v > 0 ? "Transfers tracked" : "No transfers recorded"),
      // },
      {
        key: "inventory",
        label: "Data Inventory & Mapping",
        icon: FaDatabase,
        color: "#0891b2",
        metricKey: "dataMapping",
        subText: (v) => (v > 0 ? "Assets mapped" : "Not configured"),
      },
      {
        key: "retention",
        label: "Retention Policies",
        icon: FaClock,
        color: "#d97706",
        metricKey: "retentionPolicies",
        subText: (v) => (v > 0 ? "Policies configured" : "Policies missing"),
      },
      {
        key: "dpar",
        label: "Data Subject Requests",
        icon: FaFileAlt,
        color: "#64748b",
        metricKey: "dparManagement",
        subText: (v) => (v > 0 ? "Requests managed" : "Not configured"),
      },
      {
        key: "consent",
        label: "Consent Management",
        icon: FaHandshake,
        color: "#f59e0b",
        metricKey: "consentManagement",
        subText: (v) =>
          v > 0 ? "Consents captured" : "Consent framework pending",
      },
    ],

    components: {
      /* UAE PDPL-specific components */
      encryption: (props) => <UaeSecurityEncryptionTab {...props} />,
      crossBorder: (props) => <UaeCrossBorderTransfersTab {...props} />,
      inventory: (props) => <UAEInventoryTab {...props} />,
      retention: (props) => <UaeRetentionTab {...props} />,
      policyGen: (props) => <UaePolicyGeneratorTab {...props} />,
      dpar: (props) => <UaeDsarManagementTab {...props} />,
     consent: (props) => <UaeConsentManagementTab {...props} />,
      sdf: (props) => <DPOManagementTab {...props} regulation="pdpl" />,
    },
  },
};

/* ═══════════════════════════════════════════════════════════════════
 * PLACEHOLDER MODULE
 * Rendered for GDPR / CCPA / PDPL modules until their actual
 * components are provided. Shows a clear "ready to integrate" state.
 * ═══════════════════════════════════════════════════════════════════ */
const PlaceholderModule = ({ regulation, module }) => {
  const config = REGULATION_CONFIGS[regulation];
  return (
    <div
      style={{
        padding: "60px 40px",
        textAlign: "center",
        background: "#fff",
        borderRadius: "14px",
        border: "2px dashed #E2E6EC",
      }}
    >
      <div style={{ fontSize: "3rem", marginBottom: "16px" }}>
        {config?.flag || "🌍"}
      </div>
      <h4
        style={{
          fontSize: "1.1rem",
          fontWeight: 700,
          color: "#1A2332",
          marginBottom: "8px",
        }}
      >
        {module} — {config?.label || regulation.toUpperCase()}
      </h4>
      <p
        style={{
          fontSize: "0.85rem",
          color: "#8E99A8",
          maxWidth: "400px",
          margin: "0 auto 20px",
          lineHeight: 1.6,
        }}
      >
        This module is ready for integration. Share the {config?.label}{" "}
        {module.toLowerCase()} component code and API endpoints to activate this
        section.
      </p>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px 16px",
          background: "rgba(0,149,196,0.08)",
          borderRadius: "8px",
          fontSize: "0.8rem",
          color: "#0095C4",
          fontWeight: 600,
        }}
      >
        <FaFileAlt /> Awaiting component integration
      </div>
    </div>
  );
};

/* ── SUSAN brand tokens ── */
const SUSAN = {
  primary: "#01696f",
  primaryHover: "#0c4e54",
  primaryLight: "#e6f4f4",
  primaryMid: "#cedcd8",
};

/* ── Unified design tokens ── */
const T = {
  brand: "#0095C4",
  brandLight: "rgba(0, 149, 196, 0.10)",
  cyan: "#6BCEEF",
  success: "#33A148",
  error: "#DC3545",
  white: "#FFFFFF",
  bodyBg: "#F3F5F8",
  cardBg: "#FFFFFF",
  cardShadow: "0 2px 12px rgba(0,0,0,0.08)",
  textPrimary: "#1A2332",
  textSecondary: "#5A6577",
  textMuted: "#8E99A8",
  border: "#E2E6EC",
  borderLight: "#F0F2F5",
  radiusSm: "6px",
  radiusMd: "10px",
  radiusLg: "14px",
  font: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
};

/* ── INLINE STYLES ── */
const S = {
  page: {
    background: T.bodyBg,
    minHeight: "calc(100vh - 50px)",
    paddingBottom: "20px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 28px 12px",
    borderBottom: `1px solid ${T.border}`,
    background: T.cardBg,
    marginBottom: 0,
  },
  headerTitle: {
    fontSize: "1.15rem",
    fontWeight: 700,
    color: T.textPrimary,
    margin: 0,
  },
  scorePill: (score) => ({
    padding: "6px 18px",
    borderRadius: "20px",
    fontWeight: 700,
    fontSize: "0.9rem",
    background: score > 70 ? "#d4edda" : score > 40 ? "#fff3cd" : "#f8d7da",
    color: score > 70 ? "#155724" : score > 40 ? "#856404" : "#721c24",
    border:
      score > 70
        ? "1.5px solid #c3e6cb"
        : score > 40
          ? "1.5px solid #ffc107"
          : "1.5px solid #f5c6cb",
  }),
  sdfBanner: {
    background: SUSAN.primaryLight,
    borderLeft: `4px solid ${SUSAN.primary}`,
    borderRadius: "0 8px 8px 0",
    padding: "10px 16px",
    marginBottom: "16px",
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },
  contentArea: { padding: "24px 28px 40px", fontFamily: T.font },
  sectionCard: {
    background: T.cardBg,
    borderRadius: T.radiusLg,
    padding: "24px",
    boxShadow: T.cardShadow,
    border: `1px solid ${T.borderLight}`,
    marginBottom: "24px",
  },
  sectionTitle: {
    fontSize: "15px",
    fontWeight: 600,
    color: T.textPrimary,
    marginBottom: "20px",
  },
  regTabs: {
    display: "flex",
    gap: "6px",
    marginBottom: "8px",
    flexWrap: "wrap",
  },
  regTab: (active) => ({
    padding: "8px 18px",
    borderRadius: "24px",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    border: `1px solid ${active ? T.brand : T.border}`,
    background: active ? T.brand : T.cardBg,
    color: active ? T.white : T.textSecondary,
    fontFamily: T.font,
  }),
  syncText: { fontSize: "12px", color: T.textMuted, marginBottom: "24px" },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
    marginBottom: "24px",
  },
  kpiCard: {
    background: T.cardBg,
    borderRadius: T.radiusLg,
    padding: "20px",
    boxShadow: T.cardShadow,
    border: `1px solid ${T.borderLight}`,
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  kpiHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  kpiLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: T.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  kpiIcon: (bg, fg) => ({
    width: "36px",
    height: "36px",
    borderRadius: T.radiusSm,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: bg,
    color: fg,
    fontSize: "1rem",
  }),
  kpiValue: {
    fontSize: "32px",
    fontWeight: 700,
    color: T.textPrimary,
    lineHeight: 1,
    marginBottom: "6px",
    fontVariantNumeric: "tabular-nums",
  },
  kpiSub: { fontSize: "12px", color: T.textMuted },
  compBarRow: { display: "flex", alignItems: "center", marginBottom: "14px" },
  compBarLabel: {
    width: "180px",
    fontSize: "13px",
    fontWeight: 500,
    color: T.textSecondary,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  },
  compBarTrack: {
    flex: 1,
    height: "10px",
    background: T.borderLight,
    borderRadius: "5px",
    overflow: "hidden",
    margin: "0 12px",
  },
  compBarFill: (pct) => ({
    width: `${pct}%`,
    height: "100%",
    background: T.brand,
    borderRadius: "5px",
    transition: "width 1.2s cubic-bezier(0.22,1,0.36,1)",
  }),
  compBarPct: {
    width: "40px",
    textAlign: "right",
    fontSize: "13px",
    fontWeight: 600,
    color: T.textPrimary,
    fontVariantNumeric: "tabular-nums",
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "3fr 2fr",
    gap: "20px",
    marginBottom: "24px",
  },
  activityItem: {
    display: "flex",
    gap: "12px",
    padding: "12px 0",
    borderBottom: `1px solid ${T.borderLight}`,
    alignItems: "flex-start",
  },
  activityDot: (color) => ({
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    background: color,
    marginTop: "5px",
    flexShrink: 0,
  }),
  activityText: {
    fontSize: "12.5px",
    color: T.textSecondary,
    lineHeight: 1.4,
    flex: 1,
  },
  activityDate: { fontSize: "11px", color: T.textMuted, whiteSpace: "nowrap" },
  moduleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "14px",
  },
  moduleCard: {
    background: T.cardBg,
    borderRadius: T.radiusMd,
    padding: "16px",
    boxShadow: T.cardShadow,
    border: `1px solid ${T.borderLight}`,
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  moduleCardTitle: {
    fontSize: "12.5px",
    fontWeight: 600,
    color: T.textPrimary,
    marginBottom: "12px",
  },
  moduleBarRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: "7px",
    gap: "8px",
  },
  moduleBarTrack: {
    flex: 1,
    height: "6px",
    background: T.borderLight,
    borderRadius: "3px",
    overflow: "hidden",
  },
  moduleBarFill: (pct, color) => ({
    width: `${pct}%`,
    height: "100%",
    background: color,
    borderRadius: "3px",
    transition: "width 1.2s cubic-bezier(0.22,1,0.36,1)",
  }),
  moduleBarPct: {
    fontSize: "10px",
    fontWeight: 600,
    color: T.textMuted,
    width: "28px",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  penaltyBanner: {
    background: "rgba(255, 240, 0, 0.08)",
    border: "1px solid rgba(255, 240, 0, 0.25)",
    borderRadius: T.radiusLg,
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
    gap: "16px",
  },
  quickActionBtn: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 14px",
    border: `1px solid ${T.border}`,
    borderRadius: T.radiusMd,
    background: T.cardBg,
    color: T.brand,
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s",
    width: "100%",
    textAlign: "left",
    fontFamily: T.font,
  },
  regulatorPill: {
    background: "#f0f7ff",
    border: "1px solid #cfe2ff",
    borderRadius: "8px",
    padding: "10px 14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
  },

  /* ── Stepper-specific styles (horizontal circles + connector lines) ── */
  stepperHeader: {
    background: T.cardBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "28px 32px 24px",
    fontFamily: T.font,
    borderBottom: `1px solid ${T.border}`,
    gap: "0",
  },
  stepperTab: (active, completed) => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    cursor: "pointer",
    transition: "opacity 0.2s",
    opacity: active || completed ? 1 : 0.5,
    padding: "0",
  }),
  stepperConnector: (done) => ({
    width: "60px",
    height: "3px",
    background: done ? SUSAN.primary : T.border,
    margin: "0 14px",
    borderRadius: "2px",
    transition: "background 0.3s",
    flexShrink: 0,
  }),
  stepperCircle: (active, completed) => ({
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: 700,
    flexShrink: 0,
    background: active ? SUSAN.primary : completed ? T.success : T.cardBg,
    border: active
      ? `2.5px solid ${SUSAN.primary}`
      : completed
        ? `2.5px solid ${T.success}`
        : `2.5px solid ${T.border}`,
    color: active || completed ? "#fff" : T.textMuted,
    boxShadow: active ? `0 0 0 4px rgba(1,105,111,0.18)` : "none",
    transition: "all 0.3s",
  }),
  stepperLabel: (active, completed) => ({
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  }),
  stepperLabelText: (active, completed) => ({
    fontSize: "13px",
    fontWeight: 600,
    color: active ? T.textPrimary : completed ? T.textPrimary : T.textMuted,
    transition: "color 0.2s",
    lineHeight: 1.2,
  }),
  stepperLabelDesc: (active) => ({
    fontSize: "11px",
    color: active ? T.textSecondary : T.textMuted,
    transition: "color 0.2s",
  }),
  stepContent: {
    padding: "28px 28px 20px",
    fontFamily: T.font,
    animation: "fadeIn 0.25s ease",
  },
  stepFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 28px",
    borderTop: `1px solid ${T.border}`,
    background: T.cardBg,
    fontFamily: T.font,
  },
  stepFooterLabel: {
    fontSize: "12px",
    color: T.textMuted,
    fontWeight: 500,
  },
  stepFooterBtns: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  prevBtn: (disabled) => ({
    padding: "8px 20px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: T.radiusSm,
    border: `1.5px solid ${T.border}`,
    background: "transparent",
    color: disabled ? T.textMuted : T.textSecondary,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    fontFamily: T.font,
    transition: "all 0.2s",
  }),
  nextBtn: {
    padding: "8px 22px",
    fontSize: "13px",
    fontWeight: 600,
    borderRadius: T.radiusSm,
    border: "none",
    background: SUSAN.primary,
    color: "#fff",
    cursor: "pointer",
    fontFamily: T.font,
    transition: "background 0.2s",
  },

  /* Step 1 – Jurisdiction cards */
  regCardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "16px",
    marginBottom: "24px",
  },
  regCard: (color, active, blurred) => ({
    background: T.cardBg,
    borderRadius: T.radiusLg,
    border: active
      ? `2px solid ${SUSAN.primary}`
      : `1px solid ${T.borderLight}`,
    padding: "20px 16px",
    cursor: "pointer",
    boxShadow: active ? `0 4px 20px rgba(1,105,111,0.15)` : T.cardShadow,
    transition: "all 0.3s",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: "8px",
    opacity: blurred ? 0.4 : 1,
    filter: blurred ? "grayscale(0.4)" : "none",
    transform: active ? "scale(1.02)" : "scale(1)",
  }),
  statsStrip: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "12px",
    marginBottom: "8px",
  },
  statCard: {
    background: T.cardBg,
    borderRadius: T.radiusMd,
    padding: "16px",
    boxShadow: T.cardShadow,
    border: `1px solid ${T.borderLight}`,
    textAlign: "center",
  },

  /* Step 2 – Common module cards */
  commonModuleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
    marginBottom: "8px",
  },
  commonModuleCard: {
    background: T.cardBg,
    borderRadius: T.radiusLg,
    padding: "20px",
    boxShadow: T.cardShadow,
    border: `1px solid ${T.borderLight}`,
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
  },
  regBarRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "7px",
  },
  regBarTrack: {
    flex: 1,
    height: "5px",
    background: T.borderLight,
    borderRadius: "3px",
    overflow: "hidden",
  },

  /* Step 3 – Regulation-specific columns */
  regColGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "16px",
  },
  regColHeader: (color) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
    padding: "10px 12px",
    background: T.cardBg,
    borderRadius: T.radiusMd,
    border: `1px solid ${T.borderLight}`,
    borderLeft: `4px solid ${color}`,
    boxShadow: T.cardShadow,
  }),
  specModuleCard: {
    background: T.cardBg,
    borderRadius: T.radiusMd,
    padding: "14px",
    boxShadow: T.cardShadow,
    border: `1px solid ${T.borderLight}`,
    cursor: "pointer",
    marginBottom: "10px",
    transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
  },

  /* Step 4 – Activity */
  step4TwoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    marginBottom: "24px",
  },
};

const scoreColor = (v) => (v > 70 ? "#198754" : v > 40 ? "#fd7e14" : "#dc3545");
const badgeDotColor = (b) =>
  ({
    success: "#198754",
    info: "#0dcaf0",
    warning: "#fd7e14",
    danger: "#dc3545",
    primary: "#0d6efd",
  })[b] || "#6c757d";

/* Regulation bar colors for Step 2 */
const REG_BAR_COLORS = {
  dpdp: "#035EA5",
  gdpr: "#1D4ED8",
  ccpa: "#B91C1C",
  pdpl: "#047857",
};
const formatPreviewLabel = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());

const formatPreviewValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const hiddenPreviewFields = [
  "tenantId",
  "organizationId",
  "userId",
];


const getPreviewValue = (record, keys, fallback = "-") => {
  for (const key of keys) {
    if (record?.[key] !== undefined && record?.[key] !== null && record?.[key] !== "") {
      return record[key];
    }
  }
  return fallback;
};

const FieldCard = ({ icon, label, value, accent = "#2563eb" }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "13px 14px",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      background: "#ffffff",
      minHeight: "68px",
    }}
  >
    <div
      style={{
        width: "38px",
        height: "38px",
        borderRadius: "10px",
        background: `${accent}14`,
        color: accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </div>

    <div>
      <div
        style={{
          fontSize: "11px",
          fontWeight: 800,
          color: "#64748b",
          marginBottom: "4px",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: "13px", fontWeight: 800, color: "#0f172a" }}>
        {String(value || "-")}
      </div>
    </div>
  </div>
);

const PreviewSection = ({ icon, title, subtitle, children, accent = "#2563eb" }) => (
  <div
    style={{
      border: "1px solid #dbe3ef",
      borderRadius: "16px",
      background: "#ffffff",
      overflow: "hidden",
      marginBottom: "14px",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "14px 16px",
        background: `linear-gradient(135deg, ${accent}10, #ffffff)`,
        borderBottom: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          width: "34px",
          height: "34px",
          borderRadius: "10px",
          background: `${accent}15`,
          color: accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: "14px", fontWeight: 900, color: "#1d4ed8" }}>
          {title}
        </div>
        <div style={{ fontSize: "12px", color: "#64748b" }}>{subtitle}</div>
      </div>
    </div>

    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "10px",
        padding: "14px",
      }}
    >
      {children}
    </div>
  </div>
);

const RecordPreviewDetails = ({ record }) => {
  if (!record) return null;

  const activity = getPreviewValue(record, ["processingActivityName", "activity", "processActivity", "name"]);
  const sourceModule = getPreviewValue(record, ["sourceModule"]);
  const regulation = getPreviewValue(record, ["regulations", "regulation"], "DPDP Act");
  const status = getPreviewValue(record, ["status", "currentStatus"], "Complete");

  const dataType = getPreviewValue(record, ["dataType"]);
  const classification = getPreviewValue(record, ["classification"]);
  const dataSubject = getPreviewValue(record, ["dataSubjectCategory", "dataSubjects"]);
  const owner = getPreviewValue(record, ["owner"]);
  const processor = getPreviewValue(record, ["processorThirdParty", "processor", "recipient"]);
  const location = getPreviewValue(record, ["location", "storageLocation"]);

  const purpose = getPreviewValue(record, ["purpose", "processingPurpose"]);
  const lawfulBasis = getPreviewValue(record, ["lawfulBasis", "basis"]);
  const transferType = getPreviewValue(record, ["transferType", "transfer"]);
  const transferLocation = getPreviewValue(record, ["transferLocation", "countryTransferTo", "destinationCountry"]);
  const countryStatus = getPreviewValue(record, ["countryStatus"], "Allowed Country");

  const retention = getPreviewValue(record, ["retention", "retentionPeriod"]);
  const retentionUnit = getPreviewValue(record, ["retentionUnit"], "Years");
  const reviewDate = getPreviewValue(record, ["reviewDate", "nextReviewDate"]);
  const updatedAt = getPreviewValue(record, ["updatedAt", "createdAt"]);

  return (
    <div>
      <PreviewSection
        icon={<FaUserFriends />}
        title="1. ACTIVITY OVERVIEW"
        subtitle="General information about the processing activity"
        accent="#2563eb"
      >
        <FieldCard icon={<FaUserFriends />} label="Activity" value={activity} />
        <FieldCard icon={<FaLayerGroup />} label="Source Module" value={sourceModule} />
        <FieldCard icon={<FaShieldAlt />} label="Regulation" value={regulation} />
        <FieldCard icon={<FaCheck />} label="Current Status" value={status} accent="#16a34a" />
      </PreviewSection>

      <PreviewSection
        icon={<FaDatabase />}
        title="2. PERSONAL DATA INFORMATION"
        subtitle="Details of the personal data being processed"
        accent="#2563eb"
      >
        <FieldCard icon={<FaDatabase />} label="Data Type" value={dataType} />
        <FieldCard icon={<FaShieldAlt />} label="Classification" value={classification} accent="#f59e0b" />
        <FieldCard icon={<FaUserFriends />} label="Data Subject Category" value={dataSubject} accent="#7c3aed" />
        <FieldCard icon={<FaUserShield />} label="Owner" value={owner} accent="#0891b2" />
        <FieldCard icon={<FaBuilding />} label="Processor / Third Party" value={processor} accent="#0f766e" />
        <FieldCard icon={<FaMapMarkerAlt />} label="Location" value={location} accent="#7c3aed" />
      </PreviewSection>

      <PreviewSection
        icon={<FaBalanceScale />}
        title="3. PROCESSING & LEGAL BASIS"
        subtitle="Purpose, lawful basis and transfer information"
        accent="#7c3aed"
      >
        <FieldCard icon={<FaFileAlt />} label="Purpose" value={purpose} accent="#ec4899" />
        <FieldCard icon={<FaBalanceScale />} label="Lawful Basis" value={lawfulBasis} accent="#16a34a" />
        <FieldCard icon={<FaArrowRight />} label="Transfer Type" value={transferType} accent="#f97316" />
        <FieldCard icon={<FaGlobe />} label="Transfer Location" value={transferLocation} accent="#2563eb" />
        <FieldCard icon={<FaCheck />} label="Country Status" value={countryStatus} accent="#16a34a" />
      </PreviewSection>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "14px",
        }}
      >
        <PreviewSection
          icon={<FaCalendarAlt />}
          title="4. RETENTION & POLICY"
          subtitle="Data retention and review policy"
          accent="#f59e0b"
        >
          <FieldCard icon={<FaCalendarAlt />} label="Retention Period" value={retention} />
          <FieldCard icon={<FaClock />} label="Retention Unit" value={retentionUnit} />
          <FieldCard icon={<FaCalendarAlt />} label="Review Date" value={reviewDate} />
          <FieldCard icon={<FaClock />} label="Last Updated" value={updatedAt} accent="#16a34a" />
        </PreviewSection>

        <PreviewSection
          icon={<FaShieldAlt />}
          title="5. COMPLIANCE EVIDENCE"
          subtitle="Key compliance controls and evidence status"
          accent="#2563eb"
        >
          {["Consent Obtained", "Privacy Notice Shared", "Data Mapping Completed", "Retention Policy Defined", "Security Controls Applied"].map((item) => (
            <FieldCard key={item} icon={<FaCheck />} label={item} value="Available" accent="#16a34a" />
          ))}
        </PreviewSection>
      </div>

      <div
        style={{
          marginTop: "14px",
          padding: "16px",
          borderRadius: "16px",
          border: "1px solid #bbf7d0",
          background: "linear-gradient(135deg, #f0fdf4, #ffffff)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontSize: "14px", fontWeight: 900, color: "#15803d" }}>
            Compliance Summary
          </div>
          <div style={{ fontSize: "12px", color: "#64748b" }}>
            This record supports RoPA compliance evidence and auditor review.
          </div>
        </div>

        <div style={{ fontSize: "30px", fontWeight: 900, color: "#16a34a" }}>
          92%
        </div>
      </div>
    </div>
  );
};
/* ═══════════════════════════════════════════════════════════════════
 * UNIFIED PRIVACY MODULE — Main Component
 * ═══════════════════════════════════════════════════════════════════ */
const UnifiedPrivacyModule = ({ userData }) => {
  /* ── Regulation state ── */
  const [activeRegulation, setActiveRegulation] = useState("dpdp");

  /* ── Navigation state ── */
  const [activeKey, setActiveKey] = useState("dashboard");

  /* ── UI state ── */
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSDF, setIsSDF] = useState(false);
  const [penaltyOpen, setPenaltyOpen] = useState(false);
  const [regulatorOpen, setRegulatorOpen] = useState(false);

  /* ── Wizard step state (NEW) ── */
  const [wizardStep, setWizardStep] = useState(1);

  /* ── Per-regulation compliance metrics ── */
  const [allMetrics, setAllMetrics] = useState({
    dpdp: {},
    gdpr: {},
    ccpa: {},
    pdpl: {},
  });

  /* ── Per-regulation activities ── */
  const [allActivities, setAllActivities] = useState({
    dpdp: [],
    gdpr: [],
    ccpa: [],
    pdpl: [],
  });

  /* ── Subscription (shared across regulations for now) ── */
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [trialEndDate, setTrialEndDate] = useState(null);
  const [secondTrialUsed, setSecondTrialUsed] = useState(false);
  const [subscriptionKey, setSubscriptionKey] = useState("");
  const [isActivatingKey, setIsActivatingKey] = useState(false);
  const [keyValidationError, setKeyValidationError] = useState("");

  const { profile } = useAuth();
  const organizationId = profile?.orgId;
  const getUserRegion = sessionStorage.getItem("userRegion") || "GB";
const TEST_API_BASE =
  "https://YOUR-AP-SOUTHEAST-2-API.execute-api.ap-southeast-2.amazonaws.com/prod";
  /* ── Derived: current regulation config ── */
  const regConfig = REGULATION_CONFIGS[activeRegulation];
  const complianceMetrics = allMetrics[activeRegulation] || {};
  const recentActivities = allActivities[activeRegulation] || [];

  /* Helper to update metrics for a specific regulation */
  const updateMetrics = (regulation, key, value) => {
    setAllMetrics((prev) => ({
      ...prev,
      [regulation]: { ...prev[regulation], [key]: value },
    }));
  };
  const [ropaRegulationFilter, setRopaRegulationFilter] = useState("All");
  const [dpdpInventoryRecords, setDpdpInventoryRecords] = useState([]);
  const [gdprInventoryRecords, setGdprInventoryRecords] = useState([]);
const [uaeInventoryRecords, setUaeInventoryRecords] = useState([]);
const [ccpaInventoryRecords, setCcpaInventoryRecords] = useState([]);

const [dpdpCrossBorderTransfers, setDpdpCrossBorderTransfers] = useState([]);
const [gdprCrossBorderTransfers, setGdprCrossBorderTransfers] = useState([]);
const [uaeCrossBorderTransfers, setUaeCrossBorderTransfers] = useState([]);



const [dpdpRetentionPolicies, setDpdpRetentionPolicies] = useState([]);
const [gdprRetentionPolicies, setGdprRetentionPolicies] = useState([]);
const [uaeRetentionPolicies, setUaeRetentionPolicies] = useState([]);
const [ccpaRetentionPolicies, setCcpaRetentionPolicies] = useState([]);

const [dpdpDparRequests, setDpdpDparRequests] = useState([]);
const [gdprDsarRequests, setGdprDsarRequests] = useState([]);
const [uaeDsarRequests, setUaeDsarRequests] = useState([]);
const [ccpaConsumerRequests, setCcpaConsumerRequests] = useState([]);

const [dpdpSecurityItems, setDpdpSecurityItems] = useState([]);
const [gdprSecurityMeasures, setGdprSecurityMeasures] = useState([]);
const [uaeSecurityMeasures, setUaeSecurityMeasures] = useState([]);
const [ccpaSecurityMeasures, setCcpaSecurityMeasures] = useState([]);

const [dpdpGrievances, setDpdpGrievances] = useState([]);
const [showRopaPreviewModal, setShowRopaPreviewModal] = useState(false);
const [selectedRopaRecord, setSelectedRopaRecord] = useState(null);

const [ropaReviews, setRopaReviews] = useState([]);
const [showAssignReviewerModal, setShowAssignReviewerModal] = useState(false);
const [selectedRopaForReviewer, setSelectedRopaForReviewer] = useState(null);

const [orgUsers, setOrgUsers] = useState([]);
const [loadingOrgUsers, setLoadingOrgUsers] = useState(false);
const [selectedReviewer, setSelectedReviewer] = useState(null);
const [reviewerSearch, setReviewerSearch] = useState("");
const [assigningReviewer, setAssigningReviewer] = useState(false);

  /* ── Overall score for current regulation ── */
  const overallScore = (() => {
    const modules = regConfig?.modules || [];
    if (modules.length === 0) return 0;
    const vals = modules.map((m) => complianceMetrics[m.metricKey] ?? 0);
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  })();

  /* ── Overall scores per regulation (for the compliance bars) ── */
  const getRegScore = (regKey) => {
    const cfg = REGULATION_CONFIGS[regKey];
    const mets = allMetrics[regKey] || {};
    const mods = cfg?.modules || [];
    if (mods.length === 0) return 0;
    const vals = mods.map((m) => mets[m.metricKey] ?? 0);
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  };

  const showNotification = (message, variant = "success") => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  /* ── SDF (DPDP-only feature) ── */
  useEffect(() => {
    const saved = localStorage.getItem("isSDF");
    if (saved !== null) setIsSDF(JSON.parse(saved));
  }, []);

  const handleSDFToggle = () => {
    const next = !isSDF;
    setIsSDF(next);
    localStorage.setItem("isSDF", JSON.stringify(next));
    showNotification(
      next ? "SDF mode enabled" : "SDF mode disabled",
      next ? "success" : "info",
    );
  };
  /* ── Helper: build correct auth headers per regulation ── */ // ✅ CHANGED — NEW
  const getActivityHeaders = (cfg) => ({
    "Content-Type": "application/json",
    Region: getUserRegion,
    ...(cfg.authType === "user"
      ? { UserId: userData?.["cognito:username"] }
      : { organizationId: organizationId }),
  });

const fetchDpdpInventoryForRopa = async () => {
  if (!organizationId) return;

  try {
    const res = await fetch(
      `https://vkvnrh2vze.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/inventory/list?t=${Date.now()}`,
      {
        headers: {
          Region: getUserRegion,
          organizationId: organizationId,
        },
      }
    );

    const data = await res.json();
    setDpdpInventoryRecords(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error("Failed to fetch DPDP inventory for RoPA:", error);
  }
};
const fetchGdprInventoryForRopa = async () => {
  const userId = userData?.["cognito:username"];
  if (!userId) return;

  try {
    const res = await fetch(
      `https://u3fhvjdru0.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/inventory/list?t=${Date.now()}`,
      {
        headers: {
          Region: getUserRegion,
          UserId: userId,
        },
      }
    );

    const data = await res.json();
    setGdprInventoryRecords(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error("Failed to fetch GDPR inventory for RoPA:", error);
  }
};
const fetchUaeInventoryForRopa = async () => {
  const userId = userData?.["cognito:username"];
  if (!userId) return;

  try {
    const res = await fetch(
      `https://dz51kxs2b1.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/inventory/list?t=${Date.now()}`,
      {
        headers: {
          Region: getUserRegion,
          UserId: userId,
        },
      }
    );

    const data = await res.json();
    setUaeInventoryRecords(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error("Failed to fetch UAE inventory for RoPA:", error);
  }
};
const fetchCcpaInventoryForRopa = async () => {
  const userId = userData?.["cognito:username"];
  if (!userId) return;

  try {
    const res = await fetch(
      `https://p4m3zpf7x0.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/inventory/list?t=${Date.now()}`,
      {
        headers: {
          Region: getUserRegion,
          UserId: userId,
        },
      }
    );

    const data = await res.json();
    setCcpaInventoryRecords(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error("Failed to fetch CCPA inventory for RoPA:", error);
  }
};

const fetchDpdpCrossBorderForRopa = async () => {
  if (!organizationId) return;

  try {
    const res = await fetch(
      `https://qacu7ie7ha.execute-api.ap-southeast-2.amazonaws.com/prod/api/transfers/list?t=${Date.now()}`,
      {
        headers: {
          Region: getUserRegion,
          organizationId: organizationId,
        },
      }
    );

    const data = await res.json();
    setDpdpCrossBorderTransfers(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error("Failed to fetch DPDP cross-border transfers for RoPA:", error);
  }
};
const fetchGdprCrossBorderForRopa = async () => {
  const userId = userData?.["cognito:username"];
  if (!userId) return;

  try {
    const res = await fetch(
      `https://uz4mb83j99.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/cross-border-transfers/list?t=${Date.now()}`,
      {
        headers: {
          Region: getUserRegion,
          UserId: userId,
        },
      }
    );

    const data = await res.json();
    setGdprCrossBorderTransfers(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error("Failed to fetch GDPR cross-border transfers for RoPA:", error);
  }
};
const fetchUaeCrossBorderForRopa = async () => {
  const userId = userData?.["cognito:username"];
  if (!userId) return;

  try {
    const res = await fetch(
      'https://ioec6c1z76.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/cross-border-transfers/list',
      {
        headers: {
          Region: getUserRegion,
          UserId: userId,
        },
      }
    );

    const data = await res.json();
    setUaeCrossBorderTransfers(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error("Failed to fetch UAE cross-border transfers for RoPA:", error);
  }
};



const fetchDpdpRetentionForRopa = async () => {
  if (!organizationId) return;

  try {
    const res = await fetch(
      `https://82bcadcihg.execute-api.ap-southeast-2.amazonaws.com/prod/api/retention-policy/list?t=${Date.now()}`,
      {
        headers: {
          Region: getUserRegion,
          organizationId: organizationId,
        },
      }
    );

    const data = await res.json();
    setDpdpRetentionPolicies(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error("Failed to fetch DPDP retention policies for RoPA:", error);
  }
};
const fetchGdprRetentionForRopa = async () => {
  const userId = userData?.["cognito:username"];
  if (!userId) return;

  const res = await fetch(
    `https://6d5x3n4sz8.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/retention-policy/list?t=${Date.now()}`,
    { headers: { Region: getUserRegion, UserId: userId } }
  );

  const data = await res.json();
  setGdprRetentionPolicies(Array.isArray(data) ? data : []);
};

const fetchUaeRetentionForRopa = async () => {
  const userId = userData?.["cognito:username"];
  if (!userId) return;

  const res = await fetch(
    `https://2ocd8tkrkl.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/retention-policy/list?t=${Date.now()}`,
    { headers: { Region: getUserRegion, UserId: userId } }
  );

  const data = await res.json();
  setUaeRetentionPolicies(Array.isArray(data) ? data : []);
};

const fetchCcpaRetentionForRopa = async () => {
  const userId = userData?.["cognito:username"];
  if (!userId) return;

  const res = await fetch(
    `https://qrubzcerac.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/retention-policy/list?t=${Date.now()}`,
    { headers: { Region: getUserRegion, UserId: userId } }
  );

  const data = await res.json();
  setCcpaRetentionPolicies(Array.isArray(data) ? data : []);
};

const fetchDpdpDparForRopa = async () => {
  if (!organizationId) return;

  try {
    const res = await fetch(
      `https://037wtgmzxe.execute-api.ap-southeast-2.amazonaws.com/prod/api/dsar/by-user?t=${Date.now()}`,
      {
        headers: {
          Region: getUserRegion,
          organizationId: organizationId,
        },
      }
    );

    const data = await res.json();
    setDpdpDparRequests(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error("Failed to fetch DPDP DPAR requests for RoPA:", error);
  }
};
const fetchGdprDsarForRopa = async () => {
  const userId = userData?.["cognito:username"];
  if (!userId) return;

  const res = await fetch(
    `https://p842qcume2.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/dsar/list?t=${Date.now()}`,
    { headers: { Region: getUserRegion, UserId: userId } }
  );

  const data = await res.json();
  setGdprDsarRequests(Array.isArray(data) ? data : []);
};

const fetchUaeDsarForRopa = async () => {
  const userId = userData?.["cognito:username"];
  if (!userId) return;

  const res = await fetch(
    `https://e9ra4fu7j7.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/dsar/list?t=${Date.now()}`,
    { headers: { Region: getUserRegion, UserId: userId } }
  );

  const data = await res.json();
  setUaeDsarRequests(Array.isArray(data) ? data : []);
};

const fetchCcpaConsumerRequestsForRopa = async () => {
  const userId = userData?.["cognito:username"];
  if (!userId) return;

  const res = await fetch(
    `https://4mdq06ir16.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/consumer-requests/list?t=${Date.now()}`,
    { headers: { Region: getUserRegion, UserId: userId } }
  );

  const data = await res.json();
  setCcpaConsumerRequests(Array.isArray(data) ? data : []);
};

const fetchDpdpSecurityForRopa = async () => {
  if (!organizationId) return;

  const res = await fetch(
    `https://osuykh2mcb.execute-api.ap-southeast-2.amazonaws.com/prod/api/encryption-items/list?t=${Date.now()}`,
    { headers: { Region: getUserRegion, organizationId } }
  );

  const data = await res.json();
  setDpdpSecurityItems(Array.isArray(data) ? data : []);
};

const fetchGdprSecurityForRopa = async () => {
  const userId = userData?.["cognito:username"];
  if (!userId) return;

  const res = await fetch(
    `https://niwiorih16.execute-api.ap-southeast-2.amazonaws.com/prod/gdpr/security-measures/list?t=${Date.now()}`,
    { headers: { Region: getUserRegion, UserId: userId } }
  );

  const data = await res.json();
  setGdprSecurityMeasures(Array.isArray(data) ? data : []);
};

const fetchUaeSecurityForRopa = async () => {
  const userId = userData?.["cognito:username"];
  if (!userId) return;

  const res = await fetch(
    `https://xicm1mmtob.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/access-controls/security-measures/list?t=${Date.now()}`,
    { headers: { Region: getUserRegion, UserId: userId } }
  );

  const data = await res.json();
  setUaeSecurityMeasures(Array.isArray(data) ? data : []);
};

const fetchCcpaSecurityForRopa = async () => {
  const userId = userData?.["cognito:username"];
  if (!userId) return;

  const res = await fetch(
    `https://5zr32hdoe2.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/security-measures/list?t=${Date.now()}`,
    { headers: { Region: getUserRegion, UserId: userId } }
  );

  const data = await res.json();
  setCcpaSecurityMeasures(Array.isArray(data) ? data : []);
};

const fetchDpdpGrievancesForRopa = async () => {
  if (!organizationId) return;

  try {
    const res = await fetch(
      `https://85sm4need1.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/grievances/list?t=${Date.now()}`,
      {
        headers: {
          "Content-Type": "application/json",
          Region: getUserRegion,
          organizationId: organizationId,
        },
      }
    );

    const data = await res.json();
    setDpdpGrievances(Array.isArray(data?.grievances) ? data.grievances : []);
  } catch (error) {
    console.error("Failed to fetch DPDP grievances for RoPA:", error);
  }
};
useEffect(() => {
  fetchDpdpInventoryForRopa();
  fetchGdprInventoryForRopa();
  fetchUaeInventoryForRopa();
  fetchCcpaInventoryForRopa();
  fetchDpdpCrossBorderForRopa();
  fetchGdprCrossBorderForRopa();
  fetchUaeCrossBorderForRopa();

  fetchDpdpRetentionForRopa();
  fetchGdprRetentionForRopa();
fetchUaeRetentionForRopa();
fetchCcpaRetentionForRopa();
fetchDpdpDparForRopa();
fetchGdprDsarForRopa();
fetchUaeDsarForRopa();
fetchCcpaConsumerRequestsForRopa();
fetchDpdpSecurityForRopa();
fetchGdprSecurityForRopa();
fetchUaeSecurityForRopa();
fetchCcpaSecurityForRopa();
fetchDpdpGrievancesForRopa();
fetchRopaReviews();
}, [organizationId, userData]);
  /* ── Fetch activities for each regulation that has an API ── */
  // useEffect(() => {
  //   if (!organizationId) return;
  //   Object.entries(REGULATION_CONFIGS).forEach(([regKey, cfg]) => {
  //     if (cfg.api.activitiesList) {
  //       fetch(cfg.api.activitiesList, {
  //         headers: { 'Content-Type': 'application/json', Region: getUserRegion, organizationId },
  //       })
  //         .then(r => r.json())
  //         .then(d => setAllActivities(prev => ({ ...prev, [regKey]: Array.isArray(d) ? d : [] })))
  //         .catch(console.error);
  //     }
  //   });
  // }, [organizationId]);

  /* ── Fetch activities for each regulation that has an API ── */
  useEffect(() => {
    if (!organizationId) return;

    Object.entries(REGULATION_CONFIGS).forEach(([regKey, cfg]) => {
      if (!cfg.api.activitiesList) return;

      fetch(`${cfg.api.activitiesList}?t=${Date.now()}`, {
        headers: getActivityHeaders(cfg),
      })
        .then((r) => r.json())
        .then((d) => {
          if (!Array.isArray(d)) return;

          const normalized = d.map((item) => {
            // GDPR fields: timestamp, module, details, action
            // DPDP fields: message, timeLabel, badge
            // ✅ Normalize both into one unified shape
            return {
              message: item.message || item.details || item.description || "",
              timeLabel:
                item.timeLabel ||
                (item.timestamp
                  ? new Date(item.timestamp).toLocaleString()
                  : "") ||
                item.date ||
                "",
              badge: item.badge || item.action || "info",
              // preserve originals too — so child modules that use raw fields still work
              ...item,
            };
          });

          setAllActivities((prev) => ({ ...prev, [regKey]: normalized }));
        })
        .catch(console.error);
    });
  }, [organizationId]);

  // const handleClearActivities = async () => {
  //   const cfg = regConfig;
  //   if (!cfg?.api?.activitiesClear) return;
  //   if (!window.confirm(`Clear all ${cfg.label} recent activities?`)) return;
  //   try {
  //     await fetch(cfg.api.activitiesClear, {
  //       method: 'DELETE', headers: { 'Content-Type': 'application/json', Region: getUserRegion, organizationId },
  //     });
  //     setAllActivities(prev => ({ ...prev, [activeRegulation]: [] }));
  //     showNotification('Activities cleared', 'success');
  //   } catch (e) { showNotification('Failed to clear activities', 'danger'); }
  // };
  const handleClearActivities = async () => {
    const cfg = regConfig;
    if (!cfg?.api?.activitiesClear) return;
    if (!window.confirm(`Clear all ${cfg.label} recent activities?`)) return;
    try {
      await fetch(cfg.api.activitiesClear, {
        method: "DELETE",
        headers: getActivityHeaders(cfg), // ✅ CHANGED — was hardcoded organizationId
      });
      setAllActivities((prev) => ({ ...prev, [activeRegulation]: [] }));
      showNotification("Activities cleared", "success");
    } catch (e) {
      showNotification("Failed to clear activities", "danger");
    }
  };

  /* ── Subscription check ── */
  useEffect(() => {
    if (!userData?.cognitousername) return;
    setSubscriptionLoading(true);
    fetch(
      "https://atw24p2ef8.execute-api.ap-southeast-2.amazonaws.com/prod/api/org/subscription-status",
      {
        headers: {
          organizationId: userData.cognitousername,
          Region: sessionStorage.getItem("userRegion") || "IN",
        },
      },
    )
      .then((r) => r.json())
      .then((d) => {
        setSubscriptionStatus(d.status);
        setTrialEndDate(d.trialEnd);
        setSecondTrialUsed(d.secondTrialUsed ?? false);
        if (["canceled", null].includes(d.status))
          setShowSubscriptionModal(true);
      })
      .catch(console.error)
      .finally(() => setSubscriptionLoading(false));
  }, [userData]);

  const handleStartTrial = async () => {
    try {
      const r = await fetch(
        "https://1mjbhlptp2.execute-api.eu-west-1.amazonaws.com/prod/api/billing/start-trial",
        {
          method: "POST",
          headers: {
            UserId: userData.cognitousername,
            Region: sessionStorage.getItem("userRegion") || "IN",
          },
        },
      );
      if (r.ok) window.location.reload();
      else alert("Failed to start trial");
    } catch (e) {
      alert("Failed to start trial");
    }
  };

  const handleStartSecondTrial = async () => {
    try {
      const r = await fetch(
        "https://1h9f9ci9rl.execute-api.eu-west-1.amazonaws.com/prod/api/billing/start-second-trial",
        {
          method: "POST",
          headers: {
            organizationId: userData.cognitousername,
            Region: sessionStorage.getItem("userRegion") || "IN",
          },
        },
      );
      if (r.ok) window.location.reload();
      else alert("Second trial not available");
    } catch (e) {
      alert("Failed");
    }
  };

  const handleActivateSubscriptionKey = async () => {
    if (!subscriptionKey.trim()) {
      setKeyValidationError("Please enter a key");
      return;
    }
    setIsActivatingKey(true);
    setKeyValidationError("");
    try {
      const r = await fetch(
        "https://r1rt2tzf87.execute-api.eu-west-1.amazonaws.com/prod/api/subscription/activate-key",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            organizationId: userData.cognitousername,
            Region: sessionStorage.getItem("userRegion") || "IN",
          },
          body: JSON.stringify({ subscriptionKey: subscriptionKey.trim() }),
        },
      );
      const d = await r.json();
      if (r.ok) {
        showNotification("Subscription activated!", "success");
        setShowSubscriptionModal(false);
        setSubscriptionKey("");
        window.location.reload();
      } else setKeyValidationError(d.error || "Invalid key.");
    } catch (e) {
      setKeyValidationError("Failed to activate key.");
    } finally {
      setIsActivatingKey(false);
    }
  };

  const renderSubscriptionBanner = () => {
    if (subscriptionStatus === "trialing" && trialEndDate) {
      const daysLeft = Math.floor(
        (new Date(trialEndDate) - new Date()) / 86400000,
      );
      const urgent = daysLeft < 3;
      return (
        <Alert
          variant={urgent ? "danger" : "warning"}
          className="mb-0 py-2 px-3"
          style={{ borderRadius: 0, fontSize: "0.82rem" }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <span>
              <FaClock className="me-1" />
              <strong>
                {secondTrialUsed ? "Second Trial" : "Free Trial"} Active
              </strong>{" "}
              — {daysLeft} days remaining
              {urgent && (
                <span className="ms-2 text-danger fw-bold">Ending soon!</span>
              )}
            </span>
            <Button
              size="sm"
              variant={urgent ? "danger" : "success"}
              onClick={() => setShowSubscriptionModal(true)}
            >
              Enter Key / Extend
            </Button>
          </div>
        </Alert>
      );
    }
    if (subscriptionStatus === "active") {
      return (
        <Alert
          variant="success"
          className="mb-0 py-2 px-3"
          style={{ borderRadius: 0, fontSize: "0.82rem" }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <span>
              <FaCheck className="me-1" />
              <strong>Subscription Active</strong> — Premium features enabled
            </span>
            <Button
              size="sm"
              variant="outline-success"
              onClick={() => setShowSubscriptionModal(true)}
            >
              Manage
            </Button>
          </div>
        </Alert>
      );
    }
    return null;
  };

  /* ── Penalty for current regulation ── */
  const penalty = regConfig.penaltyCalc(overallScore, isSDF);
  const penaltyLvlColor = {
    Low: "#198754",
    Medium: "#fd7e14",
    High: "#dc3545",
  }[penalty.level];

  const handleSidebarToggle = () => setIsSidebarCollapsed((p) => !p);
  useEffect(() => {
    localStorage.setItem(
      "isSidebarCollapsed",
      JSON.stringify(isSidebarCollapsed),
    );
  }, [isSidebarCollapsed]);

  if (subscriptionLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Checking subscription…</span>
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────
   * renderModuleContent — renders the correct child component
   * based on activeRegulation + activeKey
   * ───────────────────────────────────────────────────────────── */
  const renderModuleContent = () => {
    if (activeKey === "dashboard") return null;

    const components = regConfig.components;
    const renderer = components[activeKey];
    if (!renderer)
      return (
        <PlaceholderModule regulation={activeRegulation} module={activeKey} />
      );

    // ✅ STEP 1 — Define ONCE, BEFORE childProps, using the correct name
    const MODULE_METRIC_KEYS = {
      encryption: "securityMeasures",
      crossBorder: "crossBorderTransfers",
      inventory: "dataMapping",
      retention: "retentionPolicies",
      dpar: "dparManagement",
      consent: "consentManagement",
      grievance: "grievanceRedressal",
    };

    // ✅ STEP 2 — childProps uses MODULE_METRIC_KEYS (now defined above it)
    const childProps = {
      userData,
      regulation: activeRegulation,
      regulationConfig: regConfig,
      showNotification,
      region: getUserRegion,

      logToAccessLogs: (entry) =>
        logActivity({
          userData,
          orgId: organizationId,
          region: getUserRegion,
          module: regConfig.label,
          category: "Compliance",
          ...entry,
        }),

      refreshActivities: () => {
        const cfg = REGULATION_CONFIGS[activeRegulation];
        if (!cfg?.api?.activitiesList || !organizationId) return;
        fetch(`${cfg.api.activitiesList}?t=${Date.now()}`, {
          headers: getActivityHeaders(cfg),
        })
          .then((r) => r.json())
          .then((d) => {
            if (!Array.isArray(d)) return;
            const normalized = d.map((item) => ({
              message: item.message || item.details || item.description,
              timeLabel:
                item.timeLabel ||
                (item.timestamp
                  ? new Date(item.timestamp).toLocaleString()
                  : item.date),
              badge: item.badge || item.action || "info",
              ...item,
            }));
            setAllActivities((prev) => ({
              ...prev,
              [activeRegulation]: normalized,
            }));
          })
          .catch(console.error);
      },

      // ✅ MODULE_METRIC_KEYS is now defined above — no undef error
      onScoreUpdate: (score) => {
        if (MODULE_METRIC_KEYS[activeKey]) {
          updateMetrics(activeRegulation, MODULE_METRIC_KEYS[activeKey], score);
        }
      },
    };

    // ✅ STEP 3 — DELETE the old duplicate block below childProps entirely:
    // ❌ REMOVE THIS (the old moduleMetricKeys block that comes after childProps):
    // const moduleMetricKeys = { encryption: 'securityMeasures', ... };
    // if (moduleMetricKeys[activeKey]) childProps.onScoreUpdate = ...

    return <div className="p-4">{renderer(childProps)}</div>;
  };

  /* ─────────────────────────────────────────────────────────────
   * WIZARD NAVIGATION
   * ───────────────────────────────────────────────────────────── */
  const goToStep = (n) => {
  if (n >= 1 && n <= 5) setWizardStep(n);
};
  const nextStep = () => goToStep(wizardStep + 1);
  const prevStep = () => goToStep(wizardStep - 1);

  const STEPS = [
  { n: 1, label: "Jurisdictions", desc: "Regulation overview" },
  { n: 2, label: "Common Modules", desc: "Shared across all 4" },
  { n: 3, label: "Regulation-Specific", desc: "Unique per jurisdiction" },
  { n: 4, label: "RoPA Dashboard", desc: "Processing register" },
  { n: 5, label: "Activity & Actions", desc: "Feed, actions & penalties" },
];
  /* ─────────────────────────────────────────────────────────────
   * STEP 1: JURISDICTIONS
   * ───────────────────────────────────────────────────────────── */
  const renderStep1 = () => {
    const ringR = 34;
    const ringC = 2 * Math.PI * ringR;
    const ringOff = (pct) => ringC - (ringC * pct) / 100;

    return (
      <div>
        {/* Section heading */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "20px",
          }}
        >
          <span
            style={{ fontSize: "15px", fontWeight: 700, color: T.textPrimary }}
          >
            Regulation Overview
          </span>
          <span
            style={{
              background: SUSAN.primaryLight,
              color: SUSAN.primary,
              border: `1px solid ${SUSAN.primaryMid}`,
              borderRadius: "20px",
              padding: "2px 10px",
              fontSize: "11px",
              fontWeight: 600,
            }}
          >
            4 jurisdictions
          </span>
        </div>

        {/* 4 Regulation cards */}
      <div style={S.regCardGrid}>
          {Object.values(REGULATION_CONFIGS).map((cfg) => {
            const score = getRegScore(cfg.key);
            const isActive = activeRegulation === cfg.key;
            const isBlurred = !isActive;
            return (
              <div
                key={cfg.key}
                style={S.regCard(cfg.color, isActive, isBlurred)}
                onClick={() => {
                  setActiveRegulation(cfg.key);
                }}
              >
                {/* Flag */}
                <div style={{ fontSize: "2rem", lineHeight: 1 }}>
                  {cfg.flag}
                </div>

                {/* Labels */}
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: T.textPrimary,
                  }}
                >
                  {cfg.label}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    color: T.textMuted,
                    lineHeight: 1.4,
                    textAlign: "center",
                  }}
                >
                  {cfg.country}
                </div>

                {/* Score ring */}
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 80 80"
                  style={{ margin: "4px 0" }}
                >
                  <circle
                    cx="40"
                    cy="40"
                    r={ringR}
                    fill="none"
                    stroke={T.borderLight}
                    strokeWidth="8"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r={ringR}
                    fill="none"
                    stroke={
                      score > 70 ? T.success : score > 40 ? "#fd7e14" : T.error
                    }
                    strokeWidth="8"
                    strokeDasharray={ringC}
                    strokeDashoffset={ringOff(score)}
                    strokeLinecap="round"
                    transform="rotate(-90 40 40)"
                    style={{
                      transition:
                        "stroke-dashoffset 1.5s cubic-bezier(0.22,1,0.36,1)",
                    }}
                  />
                  <text
                    x="40"
                    y="44"
                    textAnchor="middle"
                    fontFamily="Inter,sans-serif"
                    fontSize="15"
                    fontWeight="700"
                    fill={T.textPrimary}
                  >
                    {score}%
                  </text>
                </svg>

                {/* Module count */}
                <div style={{ fontSize: "10px", color: T.textMuted }}>
                  {cfg.modules.length} modules
                </div>

                {/* Active indicator */}
                {isActive && (
                  <div
                    style={{
                      fontSize: "10px",
                      color: SUSAN.primary,
                      fontWeight: 600,
                      background: SUSAN.primaryLight,
                      padding: "2px 8px",
                      borderRadius: "10px",
                      border: `1px solid ${SUSAN.primaryMid}`,
                    }}
                  >
                    Active
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary stats strip */}
        <div style={S.statsStrip}>
          {[
            { value: "4", label: "Active Jurisdictions", icon: "🌍" },
            { value: "32", label: "Total Modules", icon: "📦" },
            { value: "5", label: "Common Modules", icon: "🔗" },
            { value: "12", label: "Regulation-Specific", icon: "⚖️" },
          ].map((s, i) => (
            <div key={i} style={S.statCard}>
              <div style={{ fontSize: "1.4rem", marginBottom: "6px" }}>
                {s.icon}
              </div>
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: 700,
                  color: T.textPrimary,
                  lineHeight: 1,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {s.value}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: T.textMuted,
                  marginTop: "4px",
                }}
              >
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  /* ─────────────────────────────────────────────────────────────
   * STEP 2: COMMON MODULES
   * ───────────────────────────────────────────────────────────── */
  const renderStep2 = () => {
    const COMMON_MODULES = [
      {
        key: "encryption",
        icon: FaLock,
        iconColor: "#7c3aed",
        title: "Security & Encryption",
        desc: "Controls, key management, and encryption standards across all regulations.",
        metricKey: "securityMeasures",
      },
      {
        key: "crossBorder",
        icon: FaGlobe,
        iconColor: "#0d6efd",
        title: "Cross-Border Transfers",
        desc: "Transfer mechanisms, adequacy decisions, and safeguards.",
        metricKey: "crossBorderTransfers",
      },
      {
        key: "inventory",
        icon: FaDatabase,
        iconColor: "#0891b2",
        title: "Data Inventory",
        desc: "Data asset mapping, categories, and processing activities.",
        metricKey: "dataMapping",
      },
      {
        key: "retention",
        icon: FaClock,
        iconColor: "#d97706",
        title: "Retention Policies",
        desc: "Retention schedules, disposal workflows, and policy compliance.",
        metricKey: "retentionPolicies",
      },
      {
        key: "policyGen",
        icon: FaFileAlt,
        iconColor: "#0891b2",
        title: "Policy & Notice Generator",
        desc: "Generate privacy policies and notices tailored to each regulation.",
        metricKey: null,
      },
    ];

    return (
      <div>
        {/* Section heading */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "20px",
          }}
        >
          <span
            style={{ fontSize: "15px", fontWeight: 700, color: T.textPrimary }}
          >
            Common Modules
          </span>
          <span
            style={{
              background: T.brandLight,
              color: T.brand,
              border: `1px solid rgba(0,149,196,0.2)`,
              borderRadius: "20px",
              padding: "2px 10px",
              fontSize: "11px",
              fontWeight: 600,
            }}
          >
            Shared across all 4 regulations
          </span>
          <span
            style={{
              background: SUSAN.primaryLight,
              color: SUSAN.primary,
              border: `1px solid ${SUSAN.primaryMid}`,
              borderRadius: "20px",
              padding: "2px 10px",
              fontSize: "11px",
              fontWeight: 600,
              marginLeft: "4px",
            }}
          >
            {regConfig.flag} Viewing: {regConfig.label}
          </span>
        </div>

      
<div style={S.commonModuleGrid}>
  {COMMON_MODULES
    .filter((mod) => !(activeRegulation === "ccpa" && mod.key === "crossBorder"))
    .map((mod) => {
    const IconComp = mod.icon;
    return (
              <div
                key={mod.key}
                style={S.commonModuleCard}
                onClick={() => {
                  setActiveKey(mod.key);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 24px rgba(0,0,0,0.11)";
                  e.currentTarget.style.borderColor = T.brand;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = T.cardShadow;
                  e.currentTarget.style.borderColor = T.borderLight;
                }}
              >
                {/* Icon + title */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: T.radiusSm,
                      background: `${mod.iconColor}18`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <IconComp
                      style={{ color: mod.iconColor, fontSize: "1rem" }}
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: T.textPrimary,
                        lineHeight: 1.2,
                      }}
                    >
                      {mod.title}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: T.textMuted,
                    lineHeight: 1.5,
                    marginBottom: "14px",
                  }}
                >
                  {mod.desc}
                </div>

                {/* Only the regulation selected in Step 1 */}
                {Object.values(REGULATION_CONFIGS)
                  .filter((cfg) => cfg.key === activeRegulation)
                  .map((cfg, idx, arr) => {
                  const val = mod.metricKey
                    ? (allMetrics[cfg.key]?.[mod.metricKey] ?? 0)
                    : 0;
                  const barColor = REG_BAR_COLORS[cfg.key];
                  const isSelected = cfg.key === activeRegulation;
                  return (
                    <div
                      key={cfg.key}
                      style={{
                        ...S.regBarRow,
                        ...(idx === arr.length - 1 ? { marginBottom: 0 } : {}),
                        opacity: isSelected ? 1 : 0.35,
                        transition: "opacity 0.3s",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "12px",
                          width: "18px",
                          flexShrink: 0,
                        }}
                      >
                        {cfg.flag}
                      </span>
                      <span
                        style={{
                          fontSize: "10px",
                          color: isSelected ? T.textPrimary : T.textMuted,
                          fontWeight: isSelected ? 600 : 400,
                          width: "50px",
                          flexShrink: 0,
                        }}
                      >
                        {cfg.label}
                      </span>
                      <div style={S.regBarTrack}>
                        <div
                          style={{
                            width: `${val}%`,
                            height: "100%",
                            background: isSelected ? barColor : T.border,
                            borderRadius: "3px",
                            transition:
                              "width 1.2s cubic-bezier(0.22,1,0.36,1)",
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: 600,
                          color: isSelected ? T.textPrimary : T.textMuted,
                          width: "28px",
                          textAlign: "right",
                          flexShrink: 0,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {mod.metricKey ? `${val}%` : "—"}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ─────────────────────────────────────────────────────────────
   * STEP 3: REGULATION-SPECIFIC MODULES
   * ───────────────────────────────────────────────────────────── */
  const renderStep3 = () => {
    /* Module definitions per regulation for the step 3 view */
    const REG_SPECIFIC = {
      dpdp: {
        modules: [
          {
            key: "dpar",
            icon: FaFileAlt,
            iconColor: "#64748b",
            name: "DPAR Management",
            desc: "Data Principal Access Requests — 30 day timeline",
            metricKey: "dparManagement",
            tags: [],
          },
          {
            key: "consent",
            icon: FaHandshake,
            iconColor: "#f59e0b",
            name: "Consent Management",
            desc: "Consent-centric lawful basis, notice & withdrawal",
            metricKey: "consentManagement",
            tags: [],
          },
          {
            key: "grievance",
            icon: FaComments,
            iconColor: "#6366f1",
            name: "Grievance Redressal",
            desc: "Complaint handling and escalation to DPBI",
            metricKey: "grievanceRedressal",
            tags: ["DPDP Only"],
          },
          {
            key: "sdf",
            icon: FaUserShield,
            iconColor: "#7c3aed",
            name: "DPO Management",
            desc: "Data Protection Officer obligations for SDFs",
            metricKey: null,
            tags: ["SDF"],
          },
        ],
      },
      gdpr: {
        modules: [
          {
            key: "dpar",
            icon: FaFileAlt,
            iconColor: "#1D4ED8",
            name: "DSAR Management",
            desc: "Data Subject Access Requests — 30 day response",
            metricKey: "dparManagement",
            tags: [],
          },
          {
            key: "consent",
            icon: FaHandshake,
            iconColor: "#f59e0b",
            name: "Consent Management",
            desc: "6 lawful bases, Article 7 consent requirements",
            metricKey: "consentManagement",
            tags: [],
          },
          {
            key: "sdf",
            icon: FaUserShield,
            iconColor: "#7c3aed",
            name: "DPO Management",
            desc: "Data Protection Officer appointment & obligations",
            metricKey: null,
            tags: ["DPO"],
          },
        ],
      },
      ccpa: {
        modules: [
          {
            key: "dpar",
            icon: FaFileAlt,
            iconColor: "#B91C1C",
            name: "Consumer Request",
            desc: "Consumer rights requests — 45 day response",
            metricKey: "dparManagement",
            tags: [],
          },
          {
            key: "consent",
            icon: FaHandshake,
            iconColor: "#f59e0b",
            name: "Consent & Opt-Out",
            desc: "Do Not Sell / Share, opt-out mechanisms",
            metricKey: "consentManagement",
            tags: [],
          },
        ],
      },
      pdpl: {
        modules: [
          {
            key: "dpar",
            icon: FaFileAlt,
            iconColor: "#047857",
            name: "Data Subject Rights",
            desc: "Access, correction, deletion and portability",
            metricKey: "dparManagement",
            tags: [],
          },
          {
            key: "consent",
            icon: FaHandshake,
            iconColor: "#f59e0b",
            name: "Consent Management",
            desc: "Explicit consent framework and withdrawal",
            metricKey: "consentManagement",
            tags: [],
          },
          {
            key: "sdf",
            icon: FaUserShield,
            iconColor: "#7c3aed",
            name: "DPO Management",
            desc: "Data Protection Officer requirements",
            metricKey: null,
            tags: ["DPO"],
          },
        ],
      },
    };

    return (
      <div>
        {/* Section heading */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "20px",
          }}
        >
          <span
            style={{ fontSize: "15px", fontWeight: 700, color: T.textPrimary }}
          >
            Regulation-Specific Modules
          </span>
          <span
            style={{
              background: "#fff3cd",
              color: "#856404",
              border: "1px solid #ffc107",
              borderRadius: "20px",
              padding: "2px 10px",
              fontSize: "11px",
              fontWeight: 600,
            }}
          >
            Unique to each regulation
          </span>
          <span
            style={{
              background: SUSAN.primaryLight,
              color: SUSAN.primary,
              border: `1px solid ${SUSAN.primaryMid}`,
              borderRadius: "20px",
              padding: "2px 10px",
              fontSize: "11px",
              fontWeight: 600,
              marginLeft: "4px",
            }}
          >
            {regConfig.flag} Active: {regConfig.label}
          </span>
        </div>

        <div style={S.regColGrid}>
          {Object.entries(REG_SPECIFIC).map(([regKey, { modules }]) => {
            const cfg = REGULATION_CONFIGS[regKey];
            const mets = allMetrics[regKey] || {};
            const isSelectedReg = regKey === activeRegulation;
            return (
              <div
                key={regKey}
                style={{
                  opacity: isSelectedReg ? 1 : 0.3,
                  filter: isSelectedReg ? "none" : "grayscale(0.6)",
                  transition: "all 0.3s",
                  pointerEvents: isSelectedReg ? "auto" : "none",
                }}
              >
                {/* Column header */}
                <div style={S.regColHeader(cfg.color)}>
                  <span style={{ fontSize: "1.2rem" }}>{cfg.flag}</span>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: T.textPrimary,
                      }}
                    >
                      {cfg.label}
                    </div>
                    <div style={{ fontSize: "10px", color: T.textMuted }}>
                      {cfg.country.split("(")[0].trim()}
                    </div>
                  </div>
                  <span
                    style={{
                      background: `${cfg.color}18`,
                      color: cfg.color,
                      border: `1px solid ${cfg.color}33`,
                      borderRadius: "10px",
                      padding: "1px 7px",
                      fontSize: "10px",
                      fontWeight: 700,
                    }}
                  >
                    {modules.length}
                  </span>
                </div>

                {/* Module cards */}
                {modules.map((mod) => {
                  const IconComp = mod.icon;
                  const val = mod.metricKey ? (mets[mod.metricKey] ?? 0) : null;
                  const barColor = REG_BAR_COLORS[regKey];
                  return (
                    <div
                      key={mod.key + regKey}
                      style={S.specModuleCard}
                      onClick={() => {
                        setActiveRegulation(regKey);
                        setActiveKey(mod.key);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 16px rgba(0,0,0,0.1)";
                        e.currentTarget.style.borderColor = cfg.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = T.cardShadow;
                        e.currentTarget.style.borderColor = T.borderLight;
                      }}
                    >
                      {/* Icon + name row */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px",
                          marginBottom: "6px",
                        }}
                      >
                        <div
                          style={{
                            width: "26px",
                            height: "26px",
                            borderRadius: "6px",
                            background: `${mod.iconColor}18`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            marginTop: "1px",
                          }}
                        >
                          <IconComp
                            style={{
                              color: mod.iconColor,
                              fontSize: "0.75rem",
                            }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: T.textPrimary,
                              lineHeight: 1.2,
                            }}
                          >
                            {mod.name}
                          </div>
                        </div>
                      </div>

                      {/* Desc */}
                      <div
                        style={{
                          fontSize: "10px",
                          color: T.textMuted,
                          lineHeight: 1.5,
                          marginBottom: "8px",
                        }}
                      >
                        {mod.desc}
                      </div>

                      {/* Tags */}
                      {mod.tags.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            marginBottom: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          {mod.tags.map((tag) => (
                            <span
                              key={tag}
                              style={{
                                fontSize: "9px",
                                fontWeight: 700,
                                padding: "2px 6px",
                                borderRadius: "8px",
                                background:
                                  tag === "SDF"
                                    ? SUSAN.primaryLight
                                    : tag === "DPO"
                                      ? "#ede9fe"
                                      : "#fef9c3",
                                color:
                                  tag === "SDF"
                                    ? SUSAN.primary
                                    : tag === "DPO"
                                      ? "#5b21b6"
                                      : "#713f12",
                                border:
                                  tag === "SDF"
                                    ? `1px solid ${SUSAN.primaryMid}`
                                    : tag === "DPO"
                                      ? "1px solid #c4b5fd"
                                      : "1px solid #fde68a",
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Progress bar */}
                      {val !== null && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <div
                            style={{
                              flex: 1,
                              height: "4px",
                              background: T.borderLight,
                              borderRadius: "2px",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${val}%`,
                                height: "100%",
                                background: barColor,
                                borderRadius: "2px",
                                transition:
                                  "width 1.2s cubic-bezier(0.22,1,0.36,1)",
                              }}
                            />
                          </div>
                          <span
                            style={{
                              fontSize: "10px",
                              fontWeight: 600,
                              color: T.textMuted,
                              width: "26px",
                              textAlign: "right",
                              flexShrink: 0,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {val}%
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };
/* ─────────────────────────────────────────────────────────────
 * STEP 4: RoPA DASHBOARD
 * ───────────────────────────────────────────────────────────── */
const buildRopaReviewId = (row) => {
  return `${organizationId}#${row.sourceModule}#${row.sourceRecordId}`;
};

const fetchRopaReviews = async () => {
  if (!organizationId) return;

  try {
    const res = await axios.get(
      `${TEST_API_BASE}/api/ropa-review/list?t=${Date.now()}`,
      {
        headers: {
          Region: "AP",
          organizationId,
        },
      }
    );

    setRopaReviews(Array.isArray(res.data) ? res.data : []);
  } catch (error) {
    console.error("Failed to fetch RoPA reviews:", error);
  }
};

const fetchOrgUsersForRopa = async () => {
  setLoadingOrgUsers(true);

  try {
    const res = await axios.get(`${TEST_API_BASE}/api/org/users`, {
      headers: {
        Region: "AP",
      },
    });

    setOrgUsers(Array.isArray(res.data) ? res.data : []);
  } catch (error) {
    console.error("Failed to fetch org users:", error);
    showNotification("Failed to load organization users", "danger");
  } finally {
    setLoadingOrgUsers(false);
  }
};

const openAssignReviewer = async (row) => {
  setSelectedRopaForReviewer(row);
  setSelectedReviewer(null);
  setReviewerSearch("");
  setShowAssignReviewerModal(true);
  await fetchOrgUsersForRopa();
};

const handleAssignRopaReviewer = async () => {
  if (!selectedRopaForReviewer || !selectedReviewer) return;

  setAssigningReviewer(true);

  try {
    await axios.post(
      `${TEST_API_BASE}/api/ropa-review/assign`,
      {
        organizationId,
        sourceModule: selectedRopaForReviewer.sourceModule,
        sourceRecordId: selectedRopaForReviewer.sourceRecordId,
        recordTitle:
          selectedRopaForReviewer.activity ||
          selectedRopaForReviewer.dataType ||
          "RoPA Record",
        regulation: selectedRopaForReviewer.regulations,
        assignedTo: selectedReviewer.email,
        assignedToName: selectedReviewer.name || selectedReviewer.email,
        assignedBy: profile?.email || "test-admin",
      },
      {
        headers: {
          Region: "AP",
          "Content-Type": "application/json",
        },
      }
    );

    showNotification("Reviewer assigned successfully", "success");
    setShowAssignReviewerModal(false);
    setSelectedRopaForReviewer(null);
    setSelectedReviewer(null);
    fetchRopaReviews();
  } catch (error) {
    console.error("Failed to assign reviewer:", error);
    showNotification("Failed to assign reviewer", "danger");
  } finally {
    setAssigningReviewer(false);
  }
};
const handleDownloadRopaEvidence = () => {
  const row = selectedRopaRecord;
  const record = row?.sourceRecord || row;
  if (!row && !record) return;

  // pull a value across possible keys, treating "" and "-" as empty
  const val = (keys, fallback = "N/A") => {
    for (const k of keys) {
      const v = record?.[k] ?? row?.[k];
      if (v !== undefined && v !== null && v !== "" && v !== "-")
        return Array.isArray(v) ? v.join(", ") : String(v);
    }
    return fallback;
  };

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentW = pageW - margin * 2;

  const teal = [1, 105, 111];
  const dark = [15, 23, 42];
  const muted = [100, 116, 139];
  const border = [226, 232, 240];

  // ── Header band ──
  doc.setFillColor(...teal);
  doc.rect(0, 0, pageW, 30, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(`${val(["regulations", "regulation"], "DPDP Act")} — RoPA Record`, margin, 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.text("Records of Processing Activities — Compliance Evidence", margin, 19);
  doc.setFontSize(8.5);
  doc.text(
    `Status: ${val(["status", "currentStatus"], "Complete")}    •    Generated: ${new Date().toLocaleString()}`,
    margin,
    25
  );

  let y = 40;

  const ensureSpace = (h) => {
    if (y + h > pageH - 16) {
      doc.addPage();
      y = 20;
    }
  };

  const section = (title, pairs) => {
    ensureSpace(16);
    doc.setFillColor(240, 247, 247);
    doc.rect(margin, y, contentW, 8, "F");
    doc.setTextColor(...teal);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.text(title, margin + 3, y + 5.5);
    y += 11;

    const labelW = 52;
    pairs.forEach(([label, value]) => {
      const lines = doc.splitTextToSize(String(value), contentW - labelW - 4);
      const rowH = Math.max(7, lines.length * 4.6 + 2.5);
      ensureSpace(rowH);
      doc.setDrawColor(...border);
      doc.line(margin, y - 1, margin + contentW, y - 1);
      doc.setTextColor(...muted);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text(label, margin + 2, y + 4);
      doc.setTextColor(...dark);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.text(lines, margin + labelW, y + 4);
      y += rowH;
    });
    y += 4;
  };

  section("1. Activity Overview", [
    ["Activity", val(["processingActivityName", "activity", "name"])],
    ["Source Module", val(["sourceModule"])],
    ["Regulation", val(["regulations", "regulation"], "DPDP Act")],
    ["Current Status", val(["status", "currentStatus"], "Complete")],
  ]);

  section("2. Personal Data Information", [
    ["Data Type", val(["dataType"])],
    ["Classification", val(["classification", "categories"])],
    ["Data Subject Category", val(["dataSubjectCategory", "dataSubjects", "subjects"])],
    ["Owner", val(["owner"])],
    ["Processor / Third Party", val(["processorThirdParty", "processor", "recipient"])],
    ["Location", val(["location", "storageLocation"])],
  ]);

  section("3. Processing & Legal Basis", [
    ["Purpose", val(["purpose", "processingPurpose"])],
    ["Lawful Basis", val(["lawfulBasis", "basis"])],
    ["Transfer", val(["transfer", "transferType"])],
    ["Transfer Location", val(["transferLocation", "destinationCountry"])],
    ["Country Status", val(["countryStatus"], "Allowed Country")],
  ]);

  section("4. Retention & Policy", [
    ["Retention Period", val(["retention", "retentionPeriod"])],
    ["Review Date", val(["reviewDate", "nextReviewDate"])],
    ["Last Updated", val(["updatedAt", "createdAt"])],
  ]);

  section("5. Reviewer", [
    ["Reviewer", val(["reviewerName"], "Unassigned")],
    ["Reviewer Email", val(["reviewerEmail"])],
    ["Reviewer Status", (row?.reviewerStatus || "NOT_ASSIGNED").replaceAll("_", " ")],
  ]);

  // ── Footer on every page ──
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(...muted);
    doc.text("SUSAN — RoPA Compliance Evidence", margin, pageH - 8);
    doc.text(`Page ${i} of ${pages}`, pageW - margin, pageH - 8, { align: "right" });
  }

  const safeName = val(["activity", "processingActivityName"], "ropa-record")
    .replace(/[^a-z0-9]+/gi, "-")
    .toLowerCase();
  doc.save(`${safeName}-evidence.pdf`);
  showNotification("Evidence PDF downloaded", "success");
};
const renderRoPADashboard = () => {
  const ropaCoverage = [
    {
      key: "gdpr",
      label: "GDPR",
      region: "European Union",
      color: "#1D4ED8",
      coverage: 78,
      completed: 32,
      total: 41,
    },
    {
      key: "dpdp",
      label: "DPDP Act",
      region: "India",
      color: "#33A148",
      coverage: 42,
      completed: 18,
      total: 43,
    },
    {
      key: "pdpl",
      label: "UAE PDPL",
      region: "United Arab Emirates",
      color: "#F59E0B",
      coverage: 55,
      completed: 21,
      total: 38,
    },
    {
      key: "ccpa",
      label: "CCPA",
      region: "United States California",
      color: "#7C3AED",
      coverage: 38,
      completed: 16,
      total: 42,
    },
  ];



const dpdpInventoryRopaRows = dpdpInventoryRecords.map((item) => {
 

  return {
     sourceRecord: item,
     sourceRecordId: item.id,
activity: item.processingActivityName || item.dataType || "Unnamed Processing Activity",
dataType: item.dataType || "-",
  regulations: "DPDP Act",
  sourceModule: "DPDP Inventory",
  classification: item.classification || "-",
  subjects: item.dataSubjectCategory || "-",
  processor: item.processorThirdParty || "-",
  owner: item.owner || "-",
  purpose: item.purpose || "-",
  basis: item.consentReference || "-",
  retention: item.retention || "-",
  location: item.location || "-",
transfer: "-",
transferStatus: "-",
recipientRole: "-",
recipientEntity: "-",
crossBorderLinked: "N/A",
  security:
    item.encryptedAtRest || item.encryptedInTransit
      ? "Protected"
      : "Not Protected",

  risk:
    item.classification === "Children's Personal Data"
      ? "High"
      : item.classification === "Personal Data"
      ? "Medium"
      : "Low",

  status:
    item.isCompliant === true ||
    (
      item.dataType &&
      item.owner &&
      item.dataSubjectCategory &&
      item.processorThirdParty &&
      item.purpose &&
      item.retention
    )
      ? "Complete"
      : "Incomplete",
  };
});

const gdprInventoryRopaRows = gdprInventoryRecords.map((item) => ({
  sourceRecordId: item.id,
   sourceRecord: item,
  activity:
    item.processingActivityName ||
    item.dataType ||
    "Unnamed Processing Activity",
  dataType: item.dataType || "-",
  process: "Data Inventory",
  owner: item.owner || "-",
  regulations: "GDPR",
  sourceModule: "GDPR Inventory",
  subjects: item.dataSubjectCategory || "-",
  categories: item.classification || "-",
  purpose: item.purpose || "-",
  basis: item.legalBasis || "-",transfer: "-",
transferStatus: "-",
recipientRole: "-",
recipientEntity: "-",
crossBorderLinked: "N/A",

  retention: item.retention || "-",
  processor: item.processorThirdParty || "-",
  location: item.location || "-",
  security:
    item.encryptedAtRest || item.encryptedInTransit
      ? "Protected"
      : "Not Protected",
  risk:
    item.classification === "Special Category" ||
    item.classification === "Criminal Conviction"
      ? "High"
      : item.classification === "Personal Data"
        ? "Medium"
        : "Low",
  status:
    item.processingActivityName &&
    item.dataType &&
    item.owner &&
    item.dataSubjectCategory &&
    item.processorThirdParty &&
    item.purpose &&
    item.legalBasis &&
    item.retention
      ? "Complete"
      : "Incomplete",
}));
const uaeInventoryRopaRows = uaeInventoryRecords.map((item) => ({
  sourceRecordId: item.id,
   sourceRecord: item,
  activity:
    item.processingActivityName ||
    item.dataType ||
    "Unnamed Processing Activity",
  dataType: item.dataType || "-",
  process: "Data Inventory",
  owner: item.owner || "-",
  regulations: "UAE PDPL",
  sourceModule: "UAE Inventory",
  subjects: item.dataSubjectCategory || "-",
  categories: item.classification || "-",
  purpose: item.purpose || "-",
  basis: item.legalBasis || "-",
  transfer: "From Cross-Border Module",
  retention: item.retention || "-",
  processor: item.processorThirdParty || "-",
  location: item.location || "-",
  security:
    item.encryptedAtRest || item.encryptedInTransit
      ? "Protected"
      : "Not Protected",
  risk:
    item.classification === "Sensitive Personal Data" ||
    item.classification === "Biometric Data"
      ? "High"
      : item.classification === "Personal Data"
        ? "Medium"
        : "Low",
  status:
    item.processingActivityName &&
    item.dataType &&
    item.owner &&
    item.dataSubjectCategory &&
    item.processorThirdParty &&
    item.purpose &&
    item.legalBasis &&
    item.retention
      ? "Complete"
      : "Incomplete",
}));
const ccpaInventoryRopaRows = ccpaInventoryRecords.map((item) => ({
  sourceRecordId: item.id,
   sourceRecord: item,
  activity:
    item.processingActivityName ||
    item.dataType ||
    "Unnamed Processing Activity",
  dataType: item.dataType || "-",
  process: "Data Inventory",
  owner: "-",
  regulations: "CCPA",
  sourceModule: "CCPA Inventory",
  subjects: item.dataSubjectCategory || "-",
  categories: item.ccpaCategory || "-",
  purpose: item.businessPurpose || "-",
  basis:
    item.saleStatus === "Not Sold"
      ? "Not Sold / Service Provider Use"
      : item.saleStatus || "-",
  transfer: "From Cross-Border Module",
  retention: item.retentionPeriod || "-",
  processor: item.processorThirdParty || item.thirdPartySharing || "-",
  location: item.location || "-",
  security:
    item.encryptedAtRest || item.encryptedInTransit
      ? "Protected"
      : "Not Protected",
  risk:
    item.ccpaCategory === "Sensitive Personal Information" ||
    item.ccpaCategory === "Biometric Information" ||
    item.saleStatus === "Sold"
      ? "High"
      : item.ccpaCategory === "Personal Information"
        ? "Medium"
        : "Low",
  status:
    item.processingActivityName &&
    item.dataType &&
    item.dataSubjectCategory &&
    item.processorThirdParty &&
    item.ccpaCategory &&
    item.businessPurpose &&
    item.sourcesOfData &&
    item.saleStatus &&
    item.retentionPeriod
      ? "Complete"
      : "Incomplete",
}));
  const overallCoverage = Math.round(
    ropaCoverage.reduce((sum, r) => sum + r.coverage, 0) / ropaCoverage.length
  );
const dpdpCrossBorderRopaRows = dpdpCrossBorderTransfers.map((transfer) => ({
  sourceRecordId: transfer.id,
   sourceRecord: transfer,
  activity: transfer.transferPurpose || "Cross-Border Transfer",
  dataType: "-",
  process: "Cross-Border Transfer",
  owner: "-",
  regulations: "DPDP Act",
  sourceModule: "DPDP Cross-Border",
  subjects: "-",
  categories: "-",
  purpose: transfer.transferPurpose || "-",
  basis: transfer.legalBasis || "-",
  transfer: `${transfer.source || "-"} → ${transfer.destination || "-"}`,
  transferStatus: transfer.status || "-",
  recipientEntity: transfer.entity || "-",
  recipientRole: transfer.recipientRole || "-",
  crossBorderLinked: "Standalone Record",
  retention: "-",
  processor: transfer.entity || "-",
  location: transfer.destination || "-",
  security: transfer.standardContractualClauses ? "Safeguards In Place" : "Safeguards Missing",
  risk:
    transfer.countryStatus === "Restricted" ||
    transfer.status === "Revoked"
      ? "High"
      : transfer.status === "Pending"
        ? "Medium"
        : "Low",
  status:
    transfer.type &&
    transfer.source &&
    transfer.destination &&
    transfer.entity &&
    transfer.status &&
    transfer.transferPurpose &&
    transfer.recipientRole &&
    transfer.legalBasis
      ? "Complete"
      : "Incomplete",
}));
const gdprCrossBorderRopaRows = gdprCrossBorderTransfers.map((transfer) => ({
  sourceRecordId: transfer.id,
  sourceRecord: transfer,
  activity: `${transfer.source || "-"} to ${transfer.destination || "-"} Transfer`,
  dataType: "-",
  process: "Cross-Border Transfer",
  owner: "-",
  regulations: "GDPR",
  sourceModule: "GDPR Cross-Border",
  subjects: "-",
  categories: Array.isArray(transfer.dataCategories)
    ? transfer.dataCategories.join(", ")
    : "-",
  purpose: "International data transfer",
  basis: transfer.legalMechanism || "-",
  transfer: `${transfer.source || "-"} → ${transfer.destination || "-"}`,
  transferStatus:
    transfer.adequacyDecision
      ? "Adequacy Decision"
      : transfer.sccsUsed
        ? "SCCs Used"
        : transfer.dpaApproval
          ? "DPA Approval"
          : "Review Required",
  recipientEntity: transfer.entity || "-",
  recipientRole: transfer.type || "-",
  retention: "-",
  processor: transfer.entity || "-",
  location: transfer.destination || "-",
  security:
    transfer.adequacyDecision || transfer.sccsUsed || transfer.dpaApproval
      ? "Transfer Safeguards"
      : "Safeguards Missing",
  risk:
    transfer.adequacyDecision
      ? "Low"
      : transfer.sccsUsed || transfer.dpaApproval
        ? "Medium"
        : "High",
  status:
    transfer.type &&
    transfer.source &&
    transfer.destination &&
    transfer.entity &&
    transfer.legalMechanism &&
    transfer.dataCategories &&
    transfer.dataCategories.length > 0
      ? "Complete"
      : "Incomplete",
}));
const uaeCrossBorderRopaRows = uaeCrossBorderTransfers.map((transfer) => ({
  sourceRecordId: transfer.id,
   sourceRecord: transfer,
  activity: `${transfer.source || "-"} to ${transfer.destination || "-"} Transfer`,
  dataType: "-",
  process: "Cross-Border Transfer",
  owner: "-",
  regulations: "UAE PDPL",
  sourceModule: "UAE Cross-Border",
  subjects: "-",
  categories: Array.isArray(transfer.dataCategories)
    ? transfer.dataCategories.join(", ")
    : "-",
  purpose: "International data transfer",
  basis: transfer.legalBasis || "-",
  transfer: `${transfer.source || "-"} → ${transfer.destination || "-"}`,
  transferStatus:
    transfer.adequateProtection
      ? "Adequate Protection"
      : transfer.contractualSafeguards
        ? "Contractual Safeguards"
        : transfer.tdpAuthorization
          ? "TDP Authorization"
          : "Review Required",
  recipientEntity: transfer.entity || "-",
  recipientRole: transfer.type || "-",
  retention: "-",
  processor: transfer.entity || "-",
  location: transfer.destination || "-",
  security:
    transfer.adequateProtection ||
    transfer.contractualSafeguards ||
    transfer.tdpAuthorization
      ? "Transfer Safeguards"
      : "Safeguards Missing",
  risk:
    transfer.adequateProtection
      ? "Low"
      : transfer.contractualSafeguards || transfer.tdpAuthorization
        ? "Medium"
        : "High",
  status:
    transfer.type &&
    transfer.source &&
    transfer.destination &&
    transfer.entity &&
    transfer.legalBasis &&
    transfer.dataCategories &&
    transfer.dataCategories.length > 0
      ? "Complete"
      : "Incomplete",
}));

const dpdpRetentionRopaRows = dpdpRetentionPolicies.map((policy) => ({
  sourceRecordId: policy.policyId || policy.id,
   sourceRecord: policy,
  activity: policy.category || "Retention Policy",
  dataType: policy.category || "-",
  process: "Retention Policy",
  owner: "-",
  regulations: "DPDP Act",
  sourceModule: "DPDP Retention",
  subjects: "-",
  categories: policy.category || "-",
  purpose: policy.purposeCompletionTrigger || "-",
  basis: policy.legal || "-",
  transfer: "-",
  transferStatus: "-",
  recipientEntity: "-",
  recipientRole: "-",
  retention:
    policy.periodValue && policy.periodUnit
      ? `${policy.periodValue} ${policy.periodUnit}`
      : "-",
  processor: "-",
  location: "-",
  security: policy.autoErasureEnabled
    ? "Auto-Erasure Enabled"
    : "Manual Erasure",
  risk:
    policy.hasLegalException
      ? "Medium"
      : policy.status !== "Active"
        ? "High"
        : "Low",
  status:
    policy.category &&
    policy.periodValue &&
    policy.periodUnit &&
    policy.purposeCompletionTrigger &&
    policy.retentionJustification
      ? "Complete"
      : "Incomplete",
}));
const gdprRetentionRopaRows = gdprRetentionPolicies.map((policy) => ({
  sourceRecordId: policy.policyId || policy.id,
   sourceRecord: policy,
  activity: policy.category || "Retention Policy",
  dataType: policy.category || "-",
  process: "Retention Policy",
  owner: "-",
  regulations: "GDPR",
  sourceModule: "GDPR Retention",
  subjects: "-",
  categories: policy.category || "-",
  purpose: "Retention of personal data",
  basis: policy.legalBasis || "-",
  transfer: "-",
  transferStatus: "-",
  recipientEntity: "-",
  recipientRole: "-",
  retention: policy.periodValue && policy.periodUnit ? `${policy.periodValue} ${policy.periodUnit}` : "-",
  processor: "-",
  location: "-",
  security: policy.autoDelete ? "Auto-Delete Enabled" : "Manual Delete",
  risk: policy.status !== "Active" ? "High" : "Low",
  status: policy.category && policy.periodValue && policy.periodUnit && policy.legalBasis ? "Complete" : "Incomplete",
}));

const uaeRetentionRopaRows = uaeRetentionPolicies.map((policy) => ({
  sourceRecordId: policy.policyId || policy.id,
   sourceRecord: policy,
  activity: policy.category || "Retention Policy",
  dataType: policy.category || "-",
  process: "Retention Policy",
  owner: "-",
  regulations: "UAE PDPL",
  sourceModule: "UAE Retention",
  subjects: "-",
  categories: policy.category || "-",
  purpose: "Retention of personal data",
  basis: policy.legalBasis || "-",
  transfer: "-",
  transferStatus: "-",
  recipientEntity: "-",
  recipientRole: "-",
  retention: policy.periodValue && policy.periodUnit ? `${policy.periodValue} ${policy.periodUnit}` : "-",
  processor: "-",
  location: "-",
  security: policy.autoDelete ? "Auto-Delete Enabled" : "Manual Delete",
  risk: policy.status !== "Active" ? "High" : "Low",
  status: policy.category && policy.periodValue && policy.periodUnit && policy.legalBasis ? "Complete" : "Incomplete",
}));

const ccpaRetentionRopaRows = ccpaRetentionPolicies.map((policy) => ({
  sourceRecordId: policy.policyId || policy.id,
   sourceRecord: policy,
  activity: policy.category || "Retention Policy",
  dataType: policy.category || "-",
  process: "Retention Policy",
  owner: "-",
  regulations: "CCPA",
  sourceModule: "CCPA Retention",
  subjects: "Consumers",
  categories: policy.category || "-",
  purpose: policy.businessPurpose || "-",
  basis: "Business Purpose",
  transfer: "-",
  transferStatus: "-",
  recipientEntity: "-",
  recipientRole: "-",
  retention: policy.periodValue && policy.periodUnit ? `${policy.periodValue} ${policy.periodUnit}` : "-",
  processor: "-",
  location: "-",
  security: policy.autoDelete ? "Auto-Delete Enabled" : "Manual Delete",
  risk: policy.status !== "Active" ? "High" : "Low",
  status: policy.category && policy.periodValue && policy.periodUnit && policy.businessPurpose ? "Complete" : "Incomplete",
}));

const dpdpDparRopaRows = dpdpDparRequests.map((request) => ({
  sourceRecordId: request.requestId,
   sourceRecord: request,
  activity: request.type ? `DPAR - ${request.type}` : "DPAR Request",
  dataType: "-",
  process: "Data Principal Access Request",
  owner: "-",
  regulations: "DPDP Act",
  sourceModule: "DPDP DPAR",
  subjects: request.requestor || "-",
  categories: Array.isArray(request.platforms)
    ? request.platforms.join(", ")
    : "-",
  purpose: request.details || "Data principal rights request",
  basis: "Data Principal Rights",
  transfer: "-",
  transferStatus: "-",
  recipientEntity: "-",
  recipientRole: "-",
  retention: request.dueDate ? `Due by ${request.dueDate}` : "-",
  processor: "-",
  location: "-",
  security: request.priority || "-",
  risk:
    request.status !== "Completed" &&
    request.dueDate &&
    new Date(request.dueDate) < new Date()
      ? "High"
      : request.priority === "High"
        ? "Medium"
        : "Low",
  status: request.status || "Pending",
}));

const gdprDsarRopaRows = gdprDsarRequests.map((request) => ({
  sourceRecordId: request.requestId,
    sourceRecord: request,
  activity: request.type ? `DSAR - ${request.type}` : "DSAR Request",
  dataType: "-",
  process: "Data Subject Access Request",
  owner: "-",
  regulations: "GDPR",
  sourceModule: "GDPR DSAR",
  subjects: request.dataSubject || "-",
  categories: Array.isArray(request.platforms) ? request.platforms.join(", ") : "-",
  purpose: request.details || "Data subject rights request",
  basis: "Data Subject Rights",
  transfer: "-",
  transferStatus: "-",
  recipientEntity: "-",
  recipientRole: "-",
  retention: request.dueDate ? `Due by ${request.dueDate}` : "-",
  processor: "-",
  location: "-",
  security: "-",
  risk:
    request.status !== "Completed" &&
    request.dueDate &&
    new Date(request.dueDate) < new Date()
      ? "High"
      : request.status === "Pending"
        ? "Medium"
        : "Low",
  status: request.status || "Pending",
}));

const uaeDsarRopaRows = uaeDsarRequests.map((request) => ({
  sourceRecordId: request.requestId,
     sourceRecord: request,
  activity: request.type ? `Data Subject Request - ${request.type}` : "UAE Data Subject Request",
  dataType: "-",
  process: "Data Subject Request",
  owner: "-",
  regulations: "UAE PDPL",
  sourceModule: "UAE DSAR",
  subjects: request.dataSubject || "-",
  categories: Array.isArray(request.platforms) ? request.platforms.join(", ") : "-",
  purpose: request.details || "Data subject rights request",
  basis: "Data Subject Rights",
  transfer: "-",
  transferStatus: "-",
  recipientEntity: "-",
  recipientRole: "-",
  retention: request.dueDate ? `Due by ${request.dueDate}` : "-",
  processor: "-",
  location: "-",
  security: request.verificationMethod || "-",
  risk:
    request.status !== "Completed" &&
    request.dueDate &&
    new Date(request.dueDate) < new Date()
      ? "High"
      : request.status === "Pending"
        ? "Medium"
        : "Low",
  status: request.status || "Pending",
}));

const ccpaConsumerRequestRopaRows = ccpaConsumerRequests.map((request) => ({
  sourceRecordId: request.requestId,
        sourceRecord: request,
  activity: request.type ? `Consumer Request - ${request.type}` : "CCPA Consumer Request",
  dataType: "-",
  process: "Consumer Rights Request",
  owner: "-",
  regulations: "CCPA",
  sourceModule: "CCPA Consumer Request",
  subjects: request.consumerEmail || "-",
  categories: request.source || "Consumer Rights",
  purpose: request.details || "Consumer privacy rights request",
  basis: "Consumer Rights",
  transfer: "-",
  transferStatus: "-",
  recipientEntity: "-",
  recipientRole: "-",
  retention: request.dueDate ? `Due by ${request.dueDate}` : "-",
  processor: "-",
  location: "-",
  security: request.verificationMethod || "-",
  risk:
    request.status !== "Completed" &&
    request.dueDate &&
    new Date(request.dueDate) < new Date()
      ? "High"
      : request.status === "Pending"
        ? "Medium"
        : "Low",
  status: request.status || "Pending",
}));

const dpdpSecurityRopaRows = dpdpSecurityItems.map((item) => ({
  sourceRecordId: item.id,
   sourceRecord: item,
  activity: item.dataType || "Security / Encryption Control",
  dataType: item.dataType || "-",
  process: "Security / Encryption",
  owner: "-",
  regulations: "DPDP Act",
  sourceModule: "DPDP Security",
  subjects: "-",
  categories: item.dataState || "-",
  purpose: "Security control for personal data",
  basis: "Security Safeguards",
  transfer: item.dataSource || item.dataDestination
    ? `${item.dataSource || "-"} → ${item.dataDestination || "-"}`
    : "-",
  transferStatus: item.dataState || "-",
  recipientEntity: "-",
  recipientRole: "-",
  retention: "-",
  processor: "-",
  location: item.dataLocation || item.location || "-",
  security: `${item.encryption || "-"} / ${item.access || "-"}`,
  risk: item.riskLevel || "Low",
  status: item.encryption && item.access && item.dataType ? "Complete" : "Incomplete",
}));

const gdprSecurityRopaRows = gdprSecurityMeasures.map((item) => ({
  sourceRecordId: item.id,
   sourceRecord: item,
  activity: item.measure || "Security Measure",
  
  dataType: "-",
  process: "Security / Encryption",
  owner: item.responsibleParty || "-",
  regulations: "GDPR",
  sourceModule: "GDPR Security",
  subjects: "-",
  categories: item.category || "-",
  purpose: item.description || "Security of processing",
  basis: item.gdprArticle || "Article 32",
  transfer: "-",
  transferStatus: "-",
  recipientEntity: "-",
  recipientRole: "-",
  retention: item.reviewDate ? `Review by ${item.reviewDate}` : "-",
  processor: "-",
  location: "-",
  security: item.implementation || "-",
  risk: item.implementation === "Not Implemented" ? "High" : item.implementation === "In Progress" ? "Medium" : "Low",
  status: item.measure && item.category && item.implementation ? "Complete" : "Incomplete",
}));

const uaeSecurityRopaRows = uaeSecurityMeasures.map((item) => ({
  sourceRecordId: item.id,
   sourceRecord: item,
  activity: item.measure || "Security Measure",
  dataType: "-",
  process: "Security / Encryption",
  owner: item.responsibleParty || "-",
  regulations: "UAE PDPL",
  sourceModule: "UAE Security",
  subjects: "-",
  categories: item.category || "-",
  purpose: item.description || "Security control for personal data",
  basis: item.pdplArticle || "UAE PDPL Security Measures",
  transfer: "-",
  transferStatus: "-",
  recipientEntity: "-",
  recipientRole: "-",
  retention: item.reviewDate ? `Review by ${item.reviewDate}` : "-",
  processor: "-",
  location: "-",
  security: item.implementation || "-",
  risk: item.implementation === "Not Implemented" ? "High" : item.implementation === "In Progress" ? "Medium" : "Low",
  status: item.measure && item.category && item.implementation ? "Complete" : "Incomplete",
}));

const ccpaSecurityRopaRows = ccpaSecurityMeasures.map((item) => ({
  sourceRecordId: item.id,
   sourceRecord: item,
  activity: item.measure || "Security Measure",
  dataType: "-",
  process: "Security / Encryption",
  owner: item.responsibleParty || "-",
  regulations: "CCPA",
  sourceModule: "CCPA Security",
  subjects: "Consumers",
  categories: item.category || "-",
  purpose: item.description || "Reasonable security procedures",
  basis: item.ccpaRelevance || "Consumer Personal Information Protection",
  transfer: "-",
  transferStatus: "-",
  recipientEntity: "-",
  recipientRole: "-",
  retention: item.reviewDate ? `Review by ${item.reviewDate}` : "-",
  processor: "-",
  location: "-",
  security: item.implementation || "-",
  risk: item.implementation === "Not Implemented" ? "High" : item.implementation === "In Progress" ? "Medium" : "Low",
  status: item.measure && item.category && item.implementation ? "Complete" : "Incomplete",
}));

const dpdpGrievanceRopaRows = dpdpGrievances.map((grievance) => ({
  sourceRecordId: grievance.grievanceId,
     sourceRecord: grievance,
  activity: grievance.grievanceType || "Grievance Record",
  dataType: grievance.affectedDataCategory || "-",
  process: "Grievance Redressal",
  owner: "Grievance Officer",
  regulations: "DPDP Act",
  sourceModule: "DPDP Grievance",
  subjects: grievance.complainantEmail || grievance.complainantName || "-",
  categories: grievance.affectedDataCategory || "-",
  purpose: grievance.description || "Data principal grievance handling",
  basis: "Grievance Redressal",
  transfer: "-",
  transferStatus: "-",
  recipientEntity: "-",
  recipientRole: "-",
  retention: grievance.dueDate ? `Due by ${grievance.dueDate}` : "-",
  processor: "-",
  location: "-",
  security:
    Array.isArray(grievance.evidenceUrls) && grievance.evidenceUrls.length > 0
      ? "Evidence Attached"
      : "No Evidence",
  risk:
    grievance.status === "escalated"
      ? "High"
      : grievance.priority === "high"
        ? "High"
        : grievance.priority === "medium"
          ? "Medium"
          : "Low",
  status:
    grievance.status === "resolved"
      ? "Complete"
      : grievance.status || "pending",
}));

  const pillStyle = (type) => ({
    padding: "3px 8px",
    borderRadius: "10px",
    fontSize: "10px",
    fontWeight: 700,
    background:
      type === "High"
        ? "#fde2e2"
        : type === "Medium"
          ? "#fff3cd"
          : "#d1e7dd",
    color:
      type === "High"
        ? "#b02a37"
        : type === "Medium"
          ? "#997404"
          : "#0f5132",
  });
const allRopaRows = [
  ...dpdpInventoryRopaRows,
  ...gdprInventoryRopaRows,
  ...uaeInventoryRopaRows,
  ...ccpaInventoryRopaRows,

  ...dpdpCrossBorderRopaRows,
  ...gdprCrossBorderRopaRows,
  ...uaeCrossBorderRopaRows,


  ...dpdpRetentionRopaRows,
  ...gdprRetentionRopaRows,
  ...uaeRetentionRopaRows,
  ...ccpaRetentionRopaRows,

  ...dpdpDparRopaRows,
  ...gdprDsarRopaRows,
  ...uaeDsarRopaRows,
  ...ccpaConsumerRequestRopaRows,

  ...dpdpSecurityRopaRows,
  ...gdprSecurityRopaRows,
  ...uaeSecurityRopaRows,
  ...ccpaSecurityRopaRows,

  ...dpdpGrievanceRopaRows,
];

const ropaReviewMap = new Map(
  ropaReviews.map((review) => [review.reviewId, review])
);

const enrichedRopaRows = allRopaRows.map((row) => {
  const reviewId = buildRopaReviewId(row);
  const review = ropaReviewMap.get(reviewId);

  return {
    ...row,
    reviewId,
    reviewerName: review?.assignedToName || review?.assignedTo || "",
    reviewerEmail: review?.assignedTo || "",
    reviewerStatus: review?.status || "NOT_ASSIGNED",
  };
});

const filteredRopaRows =
  ropaRegulationFilter === "All"
    ? enrichedRopaRows
    : enrichedRopaRows.filter((row) => row.regulations === ropaRegulationFilter);
const ropaTableStyles = {
  activity: { width: "210px", minWidth: "210px" },
  regulation: { width: "120px", minWidth: "120px" },
  source: { width: "150px", minWidth: "150px" },
  dataSubjects: { width: "150px", minWidth: "150px" },
  purpose: { width: "240px", minWidth: "240px" },
  lawfulBasis: { width: "260px", minWidth: "260px" },
  transfer: { width: "180px", minWidth: "180px" },
  retention: { width: "150px", minWidth: "150px" },
  status: { width: "120px", minWidth: "120px" },
  action: { width: "100px", minWidth: "100px" },
};
const exportRopaToCsv = () => {
  const headers = [
    "Activity", "Data Type", "Regulation", "Source", "Data Subjects",
    "Purpose", "Lawful Basis", "Transfer", "Retention",
    "Reviewer", "Reviewer Email", "Reviewer Status", "Status",
  ];
  const esc = (val) => {
    const s = val === null || val === undefined ? "" : String(val);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const lines = filteredRopaRows.map((r) =>
    [
      r.activity, r.dataType, r.regulations, r.sourceModule, r.subjects,
      r.purpose, r.basis, r.transfer, r.retention,
      r.reviewerName || "Unassigned", r.reviewerEmail || "",
      (r.reviewerStatus || "NOT_ASSIGNED").replaceAll("_", " "),
      r.status || "Pending",
    ].map(esc).join(",")
  );
  const csv = [headers.map(esc).join(","), ...lines].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `ropa-records-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  showNotification(`Exported ${filteredRopaRows.length} RoPA records`, "success");
};
const naify = (v) =>
  v === undefined || v === null || v === "" || v === "-" ? "N/A" : v;
 return (
  <div style={{ padding: "4px 0" }}>
    {/* Header */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "18px",
      }}
    >
      <div>
        <div style={{ fontSize: "20px", fontWeight: 800, color: "#0f172a" }}>
          Records of Processing Activities
        </div>
        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
          Unified processing register generated from inventory, retention, security, DSAR and cross-border modules.
        </div>
      </div>

  <div style={{ display: "flex", gap: "10px" }}>
        <button
          style={S.nextBtn}
          onClick={exportRopaToCsv}
          disabled={filteredRopaRows.length === 0}
        >
          <FaDownload style={{ marginRight: "6px" }} /> Export Report
        </button>
      </div>
    </div>

    {/* Executive Summary */}
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1.2fr repeat(4, 1fr)",
        gap: "14px",
        marginBottom: "18px",
      }}
    >
      <div
        style={{
          background: "linear-gradient(135deg, #01696f, #0f766e)",
          borderRadius: "16px",
          padding: "20px",
          color: "#fff",
        }}
      >
        <div style={{ fontSize: "12px", opacity: 0.85 }}>Overall RoPA Health</div>
        <div style={{ fontSize: "36px", fontWeight: 900, marginTop: "8px" }}>
          {overallCoverage}%
        </div>
        <div style={{ fontSize: "12px", opacity: 0.85 }}>
          Based on completion, lawful basis, retention and transfer evidence
        </div>
      </div>

      {[
        ["Total Records", allRopaRows.length],
      ["Reviewer Assigned", allRopaRows.filter(r => r.reviewer && r.reviewer !== "Unassigned").length],
["Pending Assignment", allRopaRows.filter(r => !r.reviewer || r.reviewer === "Unassigned").length],
   
      ].map(([label, value]) => (
        <div
          key={label}
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: "16px",
            padding: "18px",
          }}
        >
          <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 700 }}>
            {label}
          </div>
          <div style={{ fontSize: "28px", fontWeight: 900, color: "#0f172a", marginTop: "8px" }}>
            {value}
          </div>
        </div>
      ))}
    </div>

    {/* Regulation Coverage */}
    <div style={{ ...S.sectionCard, marginBottom: "18px" }}>
      <div style={{ ...S.sectionTitle, marginBottom: "16px" }}>
        Regulation Coverage
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "14px",
        }}
      >
        {ropaCoverage.map((r) => {
          const statusText =
            r.coverage >= 70 ? "Healthy" : r.coverage >= 45 ? "Needs Work" : "At Risk";

          return (
            <div
              key={r.key}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "14px",
                padding: "16px",
                background: "#fff",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: 800, color: "#0f172a" }}>
                    {r.label}
                  </div>
                  <div style={{ fontSize: "11px", color: "#64748b" }}>{r.region}</div>
                </div>

                <span
                  style={{
                    height: "24px",
                    padding: "4px 10px",
                    borderRadius: "999px",
                    fontSize: "10px",
                    fontWeight: 800,
                    background: r.coverage >= 70 ? "#dcfce7" : r.coverage >= 45 ? "#fef3c7" : "#fee2e2",
                    color: r.coverage >= 70 ? "#166534" : r.coverage >= 45 ? "#92400e" : "#991b1b",
                  }}
                >
                  {statusText}
                </span>
              </div>

              <div style={{ fontSize: "30px", fontWeight: 900, color: r.color, marginTop: "14px" }}>
                {r.coverage}%
              </div>

              <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "10px" }}>
                {r.completed} of {r.total} activities completed
              </div>

              <div style={{ height: "7px", background: "#f1f5f9", borderRadius: "999px" }}>
                <div
                  style={{
                    width: `${r.coverage}%`,
                    height: "100%",
                    background: r.color,
                    borderRadius: "999px",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {/* Register */}
    <div style={{ ...S.sectionCard, marginBottom: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <div>
          <div style={{ fontSize: "15px", fontWeight: 800, color: "#0f172a" }}>
            Processing Activities Register
          </div>
          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "3px" }}>
            Showing {filteredRopaRows.length} records
          </div>
        </div>

        <Form.Select
          value={ropaRegulationFilter}
          onChange={(e) => setRopaRegulationFilter(e.target.value)}
          style={{
            width: "220px",
            height: "38px",
            fontSize: "12px",
            borderRadius: "10px",
          }}
        >
          <option value="All">All Regulations</option>
          <option value="DPDP Act">DPDP Act</option>
          <option value="GDPR">GDPR</option>
          <option value="UAE PDPL">UAE PDPL</option>
          <option value="CCPA">CCPA</option>
        </Form.Select>
      </div>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: "14px",
          overflow: "hidden",
          maxHeight: "430px",
          overflowY: "auto",
          overflowX: "auto",
        }}
      >
      <Table
  hover
  responsive={false}
  style={{
    marginBottom: 0,
    fontSize: "12px",
    minWidth: "1680px",
    tableLayout: "fixed",
  }}
>
          <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
            <tr>
              {[
                "Activity",
                "Regulation",
                "Source",
                "Data Subjects",
                "Purpose",
                "Lawful Basis",
                "Transfer",
                "Retention",
                "Reviewer",
                "Reviewer Status",
        
                "Action",
              ].map((h) => (
                <th
                  key={h}
                  style={{
                    background: "#f8fafc",
                    color: "#334155",
                    fontSize: "11px",
                    fontWeight: 800,
                    padding: "12px",
                    borderBottom: "1px solid #e5e7eb",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredRopaRows.map((row, index) => (
              <tr key={`${row.sourceModule}-${index}`}>
                <td style={{ padding: "12px", minWidth: "210px", fontWeight: 700, color: "#0f172a" }}>
                  {naify(row.activity)}
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: 500 }}>
                    {naify(row.dataType)}
                  </div>
                </td>

                <td style={{ padding: "12px" }}>
                  <span
  style={{
    ...pillStyle(row.regulations),
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "76px",
  }}
>
  {row.regulations}
</span>
                </td>

               <td style={{ padding: "12px", color: "#475569" }}>{naify(row.sourceModule)}</td>
                <td style={{ padding: "12px", color: "#475569" }}>{naify(row.subjects)}</td>
                <td style={{ padding: "12px", color: "#475569", minWidth: "180px" }}>{naify(row.purpose)}</td>
                <td style={{ padding: "12px", color: "#475569", minWidth: "160px" }}>{naify(row.basis)}</td>
                <td style={{ padding: "12px", color: "#475569" }}>{naify(row.transfer)}</td>
                <td style={{ padding: "12px", color: "#475569" }}>{naify(row.retention)}</td>

              

               

                {/* Reviewer */}
                <td style={{ padding: "12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "12px" }}>
                        {row.reviewerName || "Unassigned"}
                      </div>
                      {row.reviewerEmail && (
                        <div style={{ color: "#64748b", fontSize: "11px" }}>
                          {row.reviewerEmail}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => openAssignReviewer(row)}
                      style={{
                        border: "1px solid #01696f",
                        background: "#fff",
                        color: "#01696f",
                        borderRadius: "6px",
                        padding: "4px 9px",
                        fontSize: "11px",
                        fontWeight: 700,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.reviewerName ? "Reassign" : "Assign"}
                    </button>
                  </div>
                </td>

                {/* Reviewer Status */}
                <td style={{ padding: "12px" }}>
                  <span
                    style={{
                      padding: "4px 9px",
                      borderRadius: "999px",
                      fontSize: "11px",
                      fontWeight: 800,
                      background:
                        row.reviewerStatus === "APPROVED"
                          ? "#dcfce7"
                          : row.reviewerStatus === "REJECTED"
                            ? "#fee2e2"
                            : row.reviewerStatus === "IN_REVIEW"
                              ? "#fef3c7"
                              : row.reviewerStatus === "ASSIGNED"
                                ? "#dbeafe"
                                : "#f1f5f9",
                      color:
                        row.reviewerStatus === "APPROVED"
                          ? "#166534"
                          : row.reviewerStatus === "REJECTED"
                            ? "#991b1b"
                            : row.reviewerStatus === "IN_REVIEW"
                              ? "#92400e"
                              : row.reviewerStatus === "ASSIGNED"
                                ? "#1e40af"
                                : "#475569",
                    }}
                  >
                    {(row.reviewerStatus || "NOT_ASSIGNED").replaceAll("_", " ")}
                  </span>
                </td>

                {/* Action */}
                <td style={{ padding: "12px" }}>
                  <button
                    onClick={() => {
                      setSelectedRopaRecord(row);
                      setShowRopaPreviewModal(true);
                    }}
                    style={{
                      border: "1px solid #bae6fd",
                      background: "#f0f9ff",
                      color: "#0369a1",
                      borderRadius: "8px",
                      padding: "6px 12px",
                      fontSize: "11px",
                      fontWeight: 800,
                    }}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  </div>
);
};
  /* ─────────────────────────────────────────────────────────────
   * STEP 4: ACTIVITY & ACTIONS
   * ───────────────────────────────────────────────────────────── */
  const renderStep4 = () => {
    return (
      <div>
        {/* Section heading */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "20px",
          }}
        >
          <span
            style={{ fontSize: "15px", fontWeight: 700, color: T.textPrimary }}
          >
            Activity & Actions
          </span>
        </div>

        {/* Two-column: Activity + Quick Actions */}
        <div style={S.step4TwoCol}>
          {/* LEFT: Recent Activity */}
          <div style={{ ...S.sectionCard, marginBottom: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "16px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: T.textPrimary,
                  }}
                >
                  Recent Activity
                </span>
                {recentActivities.length > 0 && (
                  <span
                    style={{
                      background: T.borderLight,
                      borderRadius: "10px",
                      padding: "1px 7px",
                      fontSize: "10px",
                      fontWeight: 600,
                      color: T.textMuted,
                    }}
                  >
                    {recentActivities.length}
                  </span>
                )}
              </div>
              {recentActivities.length > 0 && (
                <button
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "11px",
                    color: T.error,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                  onClick={handleClearActivities}
                >
                  <FaTrash style={{ fontSize: "10px" }} /> Clear
                </button>
              )}
            </div>
            <div style={{ maxHeight: "340px", overflowY: "auto" }}>
              {recentActivities.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "32px 16px",
                    color: T.textMuted,
                  }}
                >
                  <div style={{ fontSize: "1.8rem", marginBottom: "8px" }}>
                    🕐
                  </div>
                  <div style={{ fontSize: "12px" }}>
                    No {regConfig.label} activities yet
                  </div>
                </div>
              ) : (
                recentActivities.map((act, i) => (
                  <div
                    key={i}
                    style={{
                      ...S.activityItem,
                      ...(i === recentActivities.length - 1
                        ? { borderBottom: "none" }
                        : {}),
                    }}
                  >
                    <div
                      style={S.activityDot(badgeDotColor(act.badge || "info"))}
                    />
                    <div style={S.activityText}>{act.message}</div>
                    {act.timeLabel && (
                      <div style={S.activityDate}>{act.timeLabel}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* RIGHT: Quick Actions */}
          <div style={{ ...S.sectionCard, marginBottom: 0 }}>
            <div
              style={{
                fontSize: "14px",
                fontWeight: 600,
                color: T.textPrimary,
                marginBottom: "16px",
              }}
            >
              Quick Actions
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {[
                {
                  label: "New Data Subject Request",
                  icon: FaFileAlt,
                  key: "dpar",
                },
                {
                  label: "New Cross-Border Transfer",
                  icon: FaGlobe,
                  key: "crossBorder",
                },
                { label: "Generate Policy", icon: FaFileAlt, key: "policyGen" },
                { label: "Run Security Scan", icon: FaLock, key: "encryption" },
                { label: "Add Data Asset", icon: FaDatabase, key: "inventory" },
              ].map((action, i) => (
                <button
                  key={i}
                  style={S.quickActionBtn}
                  onClick={() => setActiveKey(action.key)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = T.brand;
                    e.currentTarget.style.color = T.white;
                    e.currentTarget.style.borderColor = T.brand;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = T.cardBg;
                    e.currentTarget.style.color = T.brand;
                    e.currentTarget.style.borderColor = T.border;
                  }}
                >
                  <action.icon style={{ fontSize: "0.85rem", flexShrink: 0 }} />
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* SDF Toggle — DPDP only */}
        {activeRegulation === "dpdp" && (
          <div
            style={{
              ...S.sectionCard,
              background: isSDF ? SUSAN.primaryLight : T.cardBg,
              border: isSDF
                ? `1.5px solid ${SUSAN.primaryMid}`
                : `1px solid ${T.borderLight}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: T.textPrimary,
                  }}
                >
                  SDF Mode
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: T.textMuted,
                    marginTop: "2px",
                  }}
                >
                  Significant Data Fiduciary (DPDP only)
                </div>
              </div>
              <Form.Check
                type="switch"
                id="sdf-toggle"
                checked={isSDF}
                onChange={handleSDFToggle}
              />
            </div>
            {isSDF && (
              <div
                style={{
                  marginTop: "8px",
                  fontSize: "11px",
                  color: SUSAN.primary,
                  background: SUSAN.primaryLight,
                  border: `1px solid ${SUSAN.primaryMid}`,
                  borderRadius: "6px",
                  padding: "6px 10px",
                }}
              >
                Additional DPIA, DPO, and audit obligations apply
              </div>
            )}
          </div>
        )}

        {/* Penalty Banner */}
        <div style={S.penaltyBanner}>
          <div
            style={{
              fontSize: "13px",
              color: T.textSecondary,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaExclamationTriangle
              style={{ color: "#d97706", fontSize: "1rem", flexShrink: 0 }}
            />
            <span>
              <strong style={{ color: T.textPrimary }}>
                {regConfig.label} Penalty Risk
              </strong>{" "}
              — {regConfig.regulator.name} can impose up to{" "}
              {regConfig.regulator.maxPenalty}. Risk:{" "}
              <strong style={{ color: penaltyLvlColor }}>
                {penalty.level}
              </strong>
              {penalty.est > 0 && (
                <>
                  {" "}
                  &bull; Est:{" "}
                  <strong>
                    {penalty.currency}
                    {penalty.est.toFixed(2)} {penalty.unit}
                  </strong>
                </>
              )}
            </span>
          </div>
          <button
            style={{
              padding: "8px 18px",
              background: T.brand,
              color: T.white,
              border: "none",
              borderRadius: T.radiusSm,
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontFamily: T.font,
            }}
            onClick={() => setPenaltyOpen((p) => !p)}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#007DAA";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = T.brand;
            }}
          >
            {penaltyOpen ? "CLOSE DETAILS" : "VIEW DETAILS"}
          </button>
        </div>

        {/* Penalty expanded */}
        {penaltyOpen && (
          <div style={S.sectionCard}>
            {penalty.est > 0 && (
              <Row className="g-3 mb-3">
                {[
                  { label: "Minimum", val: penalty.min, color: "#198754" },
                  { label: "Estimated", val: penalty.est, color: "#d97706" },
                  { label: "Maximum", val: penalty.max, color: "#dc3545" },
                ].map((item) => (
                  <Col xs={4} key={item.label}>
                    <div
                      style={{
                        background: T.borderLight,
                        borderRadius: "8px",
                        padding: "12px 16px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: T.textMuted,
                          marginBottom: "4px",
                        }}
                      >
                        {item.label}
                      </div>
                      <div
                        style={{
                          fontSize: "1.1rem",
                          fontWeight: 700,
                          color: item.color,
                        }}
                      >
                        {penalty.currency}
                        {item.val.toFixed(2)} {penalty.unit}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
            <Table
              size="sm"
              bordered
              style={{ background: T.cardBg, fontSize: "0.78rem" }}
            >
              <thead style={{ background: T.borderLight }}>
                <tr>
                  <th>Violation</th>
                  <th>Section</th>
                  <th className="text-end">Max Penalty</th>
                  <th className="text-center">Your Risk</th>
                </tr>
              </thead>
              <tbody>
                {regConfig.penalties
                  .filter((p) => !p.sdfOnly || (p.sdfOnly && isSDF))
                  .map((p, i) => (
                    <tr
                      key={i}
                      style={
                        p.sdfOnly ? { background: "rgba(255,240,0,0.08)" } : {}
                      }
                    >
                      <td>{p.violation}</td>
                      <td className="text-muted">{p.section}</td>
                      <td className="text-end fw-bold">
                        {typeof p.maxCr === "number"
                          ? `${penalty.currency}${p.maxCr}`
                          : p.maxCr}
                      </td>
                      <td className="text-center">
                        {p.metricKey ? (
                          <Badge
                            bg={
                              (complianceMetrics[p.metricKey] ?? 0) > 70
                                ? "success"
                                : "warning"
                            }
                            style={{ fontSize: "0.68rem" }}
                          >
                            {complianceMetrics[p.metricKey] ?? 0}
                          </Badge>
                        ) : (
                          <Badge bg="secondary" style={{ fontSize: "0.68rem" }}>
                            Monitored
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
            <p style={{ fontSize: "11px", color: T.textMuted, margin: 0 }}>
              Indicative only. Final penalties determined by{" "}
              {regConfig.regulator.name}.
            </p>
          </div>
        )}

        {/* Regulator */}
        <div>
          <div
            style={S.regulatorPill}
            onClick={() => setRegulatorOpen((p) => !p)}
          >
            <FaShieldAlt
              style={{
                color: SUSAN.primary,
                fontSize: "0.9rem",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                flex: 1,
                fontSize: "0.8rem",
                color: SUSAN.primary,
                fontWeight: 600,
              }}
            >
              Regulator: {regConfig.regulator.name} — Penalties up to{" "}
              {regConfig.regulator.maxPenalty}
            </span>
            {regulatorOpen ? (
              <FaChevronUp
                style={{ color: T.textMuted, fontSize: "0.72rem" }}
              />
            ) : (
              <FaChevronDown
                style={{ color: T.textMuted, fontSize: "0.72rem" }}
              />
            )}
          </div>
          {regulatorOpen && (
            <div
              style={{
                background: SUSAN.primaryLight,
                border: `1px solid ${SUSAN.primaryMid}`,
                borderTop: "none",
                borderRadius: "0 0 8px 8px",
                padding: "10px 14px",
                fontSize: "0.78rem",
                color: "#444",
                lineHeight: 1.6,
              }}
            >
              {regConfig.regulator.description}
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ─────────────────────────────────────────────────────────────
   * renderDashboard — Stepper Wizard Layout
   * (replaces the old flat dashboard layout)
   * ───────────────────────────────────────────────────────────── */
  const renderDashboard = () => {
    return (
      <div style={{ fontFamily: T.font }}>
        {/* ── Stepper Header (horizontal circles + connector lines) ── */}
        <div style={S.stepperHeader}>
          {STEPS.map((step, idx) => {
            const isActive = wizardStep === step.n;
            const isCompleted = wizardStep > step.n;
            return (
              <React.Fragment key={step.n}>
                {/* Connector line before step (except first) */}
                {idx > 0 && (
                  <div
                    style={S.stepperConnector(
                      wizardStep > step.n || wizardStep === step.n,
                    )}
                  />
                )}

                {/* Step tab */}
                <div
                  style={S.stepperTab(isActive, isCompleted)}
                  onClick={() => goToStep(step.n)}
                >
                  {/* Numbered circle */}
                  <div style={S.stepperCircle(isActive, isCompleted)}>
                    {isCompleted ? (
                      <FaCheck style={{ fontSize: "12px" }} />
                    ) : (
                      step.n
                    )}
                  </div>

                  {/* Label + description */}
                  <div style={S.stepperLabel(isActive, isCompleted)}>
                    <span style={S.stepperLabelText(isActive, isCompleted)}>
                      {step.label}
                    </span>
                    <span style={S.stepperLabelDesc(isActive)}>
                      {step.desc}
                    </span>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Step Content ── */}
        <div style={{ background: T.cardBg }}>
          <div style={S.stepContent}>
            {wizardStep === 1 && renderStep1()}
            {wizardStep === 2 && renderStep2()}
            {wizardStep === 3 && renderStep3()}
           {wizardStep === 4 && renderRoPADashboard()}
{wizardStep === 5 && renderStep4()}
          </div>

          {/* ── Step Footer ── */}
          <div style={S.stepFooter}>
            <span style={S.stepFooterLabel}>Step {wizardStep} of 5</span>
            <div style={S.stepFooterBtns}>
              <button
                style={S.prevBtn(wizardStep === 1)}
                onClick={prevStep}
                disabled={wizardStep === 1}
                onMouseEnter={(e) => {
                  if (wizardStep !== 1) {
                    e.currentTarget.style.borderColor = T.textSecondary;
                    e.currentTarget.style.color = T.textPrimary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (wizardStep !== 1) {
                    e.currentTarget.style.borderColor = T.border;
                    e.currentTarget.style.color = T.textSecondary;
                  }
                }}
              >
                ← Previous
              </button>
              <button
                style={S.nextBtn}
                onClick={
                  wizardStep === 5 ? () => setActiveKey("dashboard") : nextStep
                }
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = SUSAN.primaryHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = SUSAN.primary;
                }}
              >
                {wizardStep === 5 ? "Finish ✓" : "Next →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════════════
   * MAIN RENDER
   * ═══════════════════════════════════════════════════════════════ */
  return (
    <RegulationContext.Provider
      value={{
        activeRegulation,
        regulationConfig: regConfig,
        setActiveRegulation,
      }}
    >
      <div onContextMenu={(e) => e.preventDefault()}>
        <Row className="g-0">
          <Col
            xs={12}
            className="bg-transparent sidebar-container"
            style={{
              width: isSidebarCollapsed ? "90px" : "317px",
              transition: "width 0.3s ease",
            }}
          >
            <Menubar
              isCollapsed={isSidebarCollapsed}
              onToggle={handleSidebarToggle}
            />
          </Col>
          <Col
            className="pt-0 px-0 ps-0"
            style={{
              width: `calc(100% - ${isSidebarCollapsed ? "90px" : "317px"})`,
              transition: "width 0.3s ease",
              height: "100vh",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <NavigationBar
              userData={userData}
              isSidebarCollapsed={isSidebarCollapsed}
            />

            <div style={S.page}>
              {renderSubscriptionBanner()}

              <div className={showSubscriptionModal ? "blur-content" : ""}>
                {/* Page Header */}
                <div style={S.header}>
                  <div>
                    <h5 style={S.headerTitle}>
                      {activeKey === "dashboard"
                        ? "Unified Privacy Dashboard"
                        : `${regConfig.flag} ${regConfig.label} — ${
                            [
                              ...(regConfig.modules || []),
                              { key: "policyGen", label: "Policy Generator" },
                              { key: "sdf", label: "DPO Management" },
                            ].find((m) => m.key === activeKey)?.label ||
                            activeKey
                          }`}
                    </h5>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        color: T.textMuted,
                        marginTop: "2px",
                      }}
                    >
                      {activeKey === "dashboard"
                        ? "Privacy & Data Protection across all regulations"
                        : regConfig.fullName}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    {activeKey !== "dashboard" && (
                      <button
                        onClick={() => setActiveKey("dashboard")}
                        style={{
                          background: SUSAN.primaryLight,
                          border: `1.5px solid ${SUSAN.primaryMid}`,
                          borderRadius: "8px",
                          padding: "6px 14px",
                          fontSize: "0.8rem",
                          color: SUSAN.primary,
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        ← Dashboard
                      </button>
                    )}
                    <span style={S.scorePill(overallScore)}>
                      {regConfig.flag} {regConfig.label}: {overallScore}% | RoPA: 53%
                    </span>
                  </div>
                </div>

                {/* Content — stepper wizard when on dashboard, child component otherwise */}
                {activeKey === "dashboard" && renderDashboard()}
                {activeKey !== "dashboard" && renderModuleContent()}
              </div>
            </div>

            <BackgroundFooter isSidebarCollapsed={isSidebarCollapsed} />

            <ToastContainer position="top-end" className="p-3">
              <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={3000}
                autohide
                bg={toastVariant}
              >
                <Toast.Body className="text-white">{toastMessage}</Toast.Body>
              </Toast>
            </ToastContainer>
     <Modal
  show={showRopaPreviewModal}
  onHide={() => setShowRopaPreviewModal(false)}
  size="xl"
  centered
>
  <Modal.Body style={{ padding: 0, borderRadius: "18px", overflow: "hidden" }}>
    <div
      style={{
        background: "linear-gradient(135deg, #082f49, #075985)",
        color: "#ffffff",
        padding: "24px 28px",
        position: "relative",
      }}
    >
      <button
        onClick={() => setShowRopaPreviewModal(false)}
        style={{
          position: "absolute",
          top: "18px",
          right: "20px",
          border: "none",
          background: "transparent",
          color: "#ffffff",
          fontSize: "20px",
          cursor: "pointer",
        }}
      >
        <FaTimes />
      </button>

      <div style={{ fontSize: "24px", fontWeight: 900 }}>
        {selectedRopaRecord?.regulations || "DPDP"} Inventory Record Preview
      </div>
      <div style={{ fontSize: "13px", opacity: 0.9, marginTop: "6px" }}>
        Review processing activity details and compliance evidence
      </div>

      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "7px",
          marginTop: "12px",
          padding: "6px 13px",
          borderRadius: "999px",
          background: "#dcfce7",
          color: "#166534",
          fontSize: "12px",
          fontWeight: 800,
        }}
      >
        <FaCheck /> {selectedRopaRecord?.status || "Complete"}
      </div>
    </div>

    <div
      style={{
        padding: "16px 22px 20px",
        background: "#f8fafc",
        maxHeight: "72vh",
        overflowY: "auto",
      }}
    >
      <RecordPreviewDetails record={selectedRopaRecord?.sourceRecord || selectedRopaRecord} />
    </div>

    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: "10px",
        padding: "14px 22px",
        borderTop: "1px solid #e5e7eb",
        background: "#ffffff",
      }}
    >
    <button style={S.prevBtn(false)} onClick={handleDownloadRopaEvidence}>
        <FaDownload /> Download Evidence
      </button>
      <button
        onClick={() => setShowRopaPreviewModal(false)}
        style={{
          ...S.nextBtn,
          background: "#0f3b70",
          minWidth: "100px",
        }}
      >
        Close
      </button>
    </div>
  </Modal.Body>
</Modal>
<Modal
  show={showAssignReviewerModal}
  onHide={() => setShowAssignReviewerModal(false)}
  size="lg"
  centered
>
  <Modal.Header closeButton>
    <Modal.Title>Assign RoPA Reviewer</Modal.Title>
  </Modal.Header>

  <Modal.Body>
    <div style={{ border: "1px solid #e5e7eb", borderRadius: "12px", padding: "14px", marginBottom: "16px", background: "#f8fafc" }}>
      <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 700 }}>Selected Record</div>
      <div style={{ fontWeight: 800 }}>{selectedRopaForReviewer?.activity || "-"}</div>
      <div style={{ fontSize: "12px", color: "#64748b" }}>
        {selectedRopaForReviewer?.regulations} · {selectedRopaForReviewer?.sourceModule}
      </div>
    </div>

    <Form.Group className="mb-3">
      <Form.Label>Search Reviewer</Form.Label>
      <Form.Control
        value={reviewerSearch}
        onChange={(e) => setReviewerSearch(e.target.value)}
        placeholder="Search by name or email..."
      />
    </Form.Group>

    <div style={{ border: "1px solid #e5e7eb", borderRadius: "10px", maxHeight: "260px", overflowY: "auto" }}>
      {loadingOrgUsers ? (
        <div style={{ textAlign: "center", padding: "24px" }}>
          <Spinner animation="border" size="sm" />
        </div>
      ) : (
        orgUsers
          .filter((u) => {
            const q = reviewerSearch.toLowerCase();
            return !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
          })
          .map((u) => {
            const selected = selectedReviewer?.email === u.email;

            return (
              <div
                key={u.email}
                onClick={() => setSelectedReviewer(selected ? null : u)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 14px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f1f5f9",
                  background: selected ? "#e6f4f4" : "#fff",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: "13px" }}>{u.name || u.email}</div>
                  <div style={{ color: "#64748b", fontSize: "12px" }}>{u.email}</div>
                </div>

                {selected && <FaCheck color="#01696f" />}
              </div>
            );
          })
      )}
    </div>
  </Modal.Body>

  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowAssignReviewerModal(false)}>
      Cancel
    </Button>
    <Button
      onClick={handleAssignRopaReviewer}
      disabled={!selectedReviewer || assigningReviewer}
      style={{ background: "#01696f", borderColor: "#01696f", fontWeight: 700 }}
    >
      {assigningReviewer ? "Assigning..." : "Assign Reviewer"}
    </Button>
  </Modal.Footer>
</Modal>
          </Col>
        </Row>

        {/* Subscription Modal */}
        {showSubscriptionModal && (
          <div className="popup-modal">
            <div className="popup-modal-content">
              {subscriptionStatus === "active" ? (
                <div className="text-center">
                  <h5>Premium Subscription Active</h5>
                  <p>
                    Your subscription is active with full access to compliance
                    features.
                  </p>
                  <div className="d-flex gap-2 justify-content-center mt-4">
                    <Button
                      variant="secondary"
                      onClick={() => setShowSubscriptionModal(false)}
                    >
                      Close
                    </Button>
                    <Button
                      variant="outline-primary"
                      onClick={() =>
                        window.open("mailto:susan@srql.com", "_blank")
                      }
                    >
                      Contact Support
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <h5 className="text-center mb-4">
                    {subscriptionStatus === "canceled" ? (
                      <>
                        <span>Trial Has Ended</span>
                        <br />
                        <span style={{ color: "orange" }}>
                          {!secondTrialUsed
                            ? "Subscription expired. Enter subscription key to continue."
                            : ""}
                        </span>
                      </>
                    ) : !subscriptionStatus ? (
                      <>
                        <span>Unlock Advanced Compliance</span>
                        <br />
                        Start your trial or enter subscription key!
                      </>
                    ) : (
                      <>
                        <span>Premium Module Access Required</span>
                        <br />
                        <span style={{ color: "red" }}>
                          Enter subscription key to access features.
                        </span>
                      </>
                    )}
                  </h5>
                  <div className="d-flex gap-3 justify-content-center mb-4">
                    {!subscriptionStatus && (
                      <Button variant="success" onClick={handleStartTrial}>
                        Start Trial (15 days)
                      </Button>
                    )}
                    {subscriptionStatus &&
                      subscriptionStatus !== "canceled" &&
                      !secondTrialUsed && (
                        <Button
                          variant="warning"
                          onClick={handleStartSecondTrial}
                        >
                          Try Again (15 more days)
                        </Button>
                      )}
                  </div>
                  <div className="mb-4">
                    <h6>Enter Subscription Key</h6>
                    <p className="text-muted small">
                      Have a subscription key? Enter it below to activate
                      premium features.
                    </p>
                    <div className="mb-3">
                      <input
                        type="text"
                        className={`form-control text-monospace${keyValidationError ? " is-invalid" : ""}`}
                        value={subscriptionKey}
                        onChange={(e) => {
                          setSubscriptionKey(e.target.value.trim());
                          setKeyValidationError("");
                        }}
                        disabled={isActivatingKey}
                      />
                      {keyValidationError && (
                        <div className="invalid-feedback d-block">
                          {keyValidationError}
                        </div>
                      )}
                      <small className="text-muted">
                        Enter your complete subscription key
                      </small>
                    </div>
                    <div className="bg-light p-3 rounded mb-3">
                      <h6 className="small mb-2">Need a Subscription Key?</h6>
                      <p className="small mb-0">
                        Contact <strong>susan@srql.com</strong> or visit our
                        admin portal for key generation.
                      </p>
                    </div>
                  </div>
                  <div className="d-flex gap-2 justify-content-center">
                    <Button
                      variant="primary"
                      onClick={handleActivateSubscriptionKey}
                      disabled={
                        isActivatingKey ||
                        !subscriptionKey.trim() ||
                        subscriptionKey.trim().length < 20
                      }
                    >
                      {isActivatingKey ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Activating
                        </>
                      ) : (
                        <>
                          <FaKey className="me-2" />
                          Activate Subscription
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </RegulationContext.Provider>
  );
};

export default UnifiedPrivacyModule;
